import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import './ProductionReport.css';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import Print from '../../../Common_Files/Print';

// Import Chart.js components separately
import {
  Chart,
  BarController,
  BarElement,
  LineController,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  Filler
} from 'chart.js';
import { Chart as ChartJS } from 'chart.js';
// Register ALL required components
Chart.register(
  BarController,
  BarElement,
  LineController,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  Filler
);

// Also register as ChartJS for compatibility
ChartJS.register(
  BarController,
  BarElement,
  LineController,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  Filler
);

const API_BASE = 'https://18.227.126.214/api/production-report';

// ---------------------------------------------------------------------------
// Hard-coded section metadata -- this MUST match the `name`/`hasPerformance`
// values produced by SECTION_DEFS in productionReportRoutes.js on the
// backend. There are always exactly 18 sections, regardless of whether a
// given section has any data in the currently selected date range.
// ---------------------------------------------------------------------------
const SECTION_META = {
  'RIGID MOLDING - SEATS': { group: 'RIGID MOLDING', color: '#1f5fa8' },
  'RIGID MOLDING - BODY': { group: 'RIGID MOLDING', color: '#3f7cc7' },
  'RIGID MOLDING - TACKING STRIP': { group: 'RIGID MOLDING', color: '#7fa8e0' },
  'RIGID MOLDING - TOTAL': { group: 'RIGID MOLDING', color: '#14396b' },

  'FLEX MOLDING - SEATS': { group: 'FLEX MOLDING', color: '#1f9d55' },
  'FLEX MOLDING - BODY': { group: 'FLEX MOLDING', color: '#4cbf77' },
  'FLEX MOLDING - TOTAL': { group: 'FLEX MOLDING', color: '#12602f' },

  'SANDING RIGID - SEATS': { group: 'SANDING RIGID', color: '#d9314e' },
  'SANDING RIGID - BODY': { group: 'SANDING RIGID', color: '#ea5f76' },
  'SANDING RIGID - TACKING STRIP': { group: 'SANDING RIGID', color: '#f39aab' },
  'SANDING RIGID - TOTAL': { group: 'SANDING RIGID', color: '#8f1330' },

  'DRILL TAP RIGID - SEATS': { group: 'DRILL TAP RIGID', color: '#e0731c' },
  'DRILL TAP RIGID - BODY': { group: 'DRILL TAP RIGID', color: '#f0954a' },
  'DRILL TAP RIGID - TOTAL': { group: 'DRILL TAP RIGID', color: '#a34e0c' },

  'TRIMMING FLEX - SEATS': { group: 'TRIMMING FLEX', color: '#7b3fc4' },
  'TRIMMING FLEX - BODY': { group: 'TRIMMING FLEX', color: '#a074d6' },
  'TRIMMING FLEX - TOTAL': { group: 'TRIMMING FLEX', color: '#4d1f8f' },

  'ASSEMBLY - TOTAL': { group: 'ASSEMBLY', color: '#0d9488' }
};

// hasPerformance: true  -> chart shows Target vs Produced + Efficiency %
//                 false -> chart shows Produced (+ Scrap) only, no target
const GROUP_META = {
  'RIGID MOLDING': { icon: 'fa-cube', color: '#1f5fa8', hasPerformance: true },
  'FLEX MOLDING': { icon: 'fa-cubes', color: '#1f9d55', hasPerformance: true },
  'SANDING RIGID': { icon: 'fa-sand', color: '#d9314e', hasPerformance: false },
  'DRILL TAP RIGID': { icon: 'fa-cogs', color: '#e0731c', hasPerformance: false },
  'TRIMMING FLEX': { icon: 'fa-cut', color: '#7b3fc4', hasPerformance: false },
  'ASSEMBLY': { icon: 'fa-tools', color: '#0d9488', hasPerformance: false }
};

const GROUP_ORDER = ['RIGID MOLDING', 'FLEX MOLDING', 'SANDING RIGID', 'DRILL TAP RIGID', 'TRIMMING FLEX', 'ASSEMBLY'];

// Groups where "no data submitted" isn't a meaningful signal -- for these
// groups the Data Completeness Check (and the print report) only surfaces
// the "0 produced" / "partial zero" warnings, never the "no data" ones.
// Sections where "no data submitted" isn't a meaningful signal -- for these
// EXACT sections (not necessarily their whole department) the Data
// Completeness Check (and the print report) only surfaces "0 produced" /
// "partial-zero" warnings, never "no data" ones. Assembly's only section
// behaves this way, and so does Rigid Molding's Tacking Strip sub-section
// specifically -- Rigid Molding's Seats and Body still get full tracking.
const SECTIONS_HIDE_MISSING = ['ASSEMBLY - TOTAL', 'RIGID MOLDING - TACKING STRIP'];
const shouldHideMissingForSection = (section) => SECTIONS_HIDE_MISSING.includes(section);

// ---------------------------------------------------------------------------
// Day-wise chart groups
// ---------------------------------------------------------------------------
// Groups that get the multi-color, day-wise, PER-OPERATOR stacked bar chart
// (colored by operator so it's easy to see who did better on which day),
// with a cumulative-progress line laid over the top. Based on Produced only
// -- these groups have no meaningful Target.
const OPERATOR_DAYWISE_GROUPS = ['SANDING RIGID', 'DRILL TAP RIGID', 'TRIMMING FLEX', 'ASSEMBLY'];

// Groups that instead get a day-wise Target vs Produced chart, broken out
// by sub-section (Seats / Body / Tacking Strip), NOT by operator.
const TARGET_DAYWISE_GROUPS = ['RIGID MOLDING', 'FLEX MOLDING'];

// Fixed colors by BODY PART -- deliberately NOT tied to operator identity,
// so the same part always reads the same color no matter who worked it.
// Seat = light, Body = dark, Tacking Strip = a mid tone in between.
const SUBPART_BAR_COLORS = {
  'SEATS': '#a9c9ef',
  'BODY': '#123a68',
  'TACKING STRIP': '#4f7cb5',
  'TOTAL': '#0d9488'
};
const getSubpartBarColor = (subLabel) => SUBPART_BAR_COLORS[subLabel] || '#6f42c1';

// Operators are told apart on the trend line by DASH PATTERN + legend label,
// not by color -- so color never implies "this operator" anywhere in these
// charts. Cycles if there are more operators than patterns.
const LINE_DASH_PATTERNS = [[], [7, 4], [2, 2], [7, 4, 2, 4], [1, 3], [10, 4, 2, 4]];
const getOperatorDash = (operatorIndex) => LINE_DASH_PATTERNS[operatorIndex % LINE_DASH_PATTERNS.length];

// Fixed colors for Target/Produced by sub-part, used in the RIGID/FLEX
// MOLDING day-wise chart. Target = light/background, Produced = dark/
// foreground, drawn overlapping in the same bar slot (not side-by-side).
const SUBPART_TARGET_PRODUCED_COLORS = {
  'SEATS': { target: 'rgba(31,95,168,0.28)', produced: '#1f5fa8' },
  'BODY': { target: 'rgba(214,102,20,0.28)', produced: '#d66614' },
  'TACKING STRIP': { target: 'rgba(31,157,85,0.28)', produced: '#1f9d55' }
};
const subpartColor = (subLabel, kind) => {
  const entry = SUBPART_TARGET_PRODUCED_COLORS[subLabel];
  if (entry) return entry[kind];
  return kind === 'target' ? 'rgba(111,66,193,0.28)' : '#6f42c1';
};

// ---------------------------------------------------------------------------
// Operator-name normalization
// ---------------------------------------------------------------------------
// Operator is free-typed in some workflows, so the same person can show up
// as "Madhu Bala" one day and "MADHU BALA" another -- that fragments totals
// across two rows on the chart/legend. Grouping by a case+whitespace
// normalized key merges those. NOTE: this only merges exact matches once
// case/spacing is ignored -- it deliberately does NOT try to merge things
// like "Avalyn" vs "Avalyn Corneleus" or "Barathy Sanmugan" vs "VIJAYABARATHY
// SANMUGAN", since those are genuinely different strings and guessing they're
// the same person risks silently combining two different operators' numbers.
const normalizeOperatorKey = (name) => String(name || 'Unassigned').trim().replace(/\s+/g, ' ').toUpperCase();

// When merging casing variants, prefer whichever isn't ALL CAPS as the
// display label (reads better), otherwise keep the first one seen.
const pickBetterDisplayName = (existing, candidate) => {
  if (!existing) return candidate;
  const isAllCaps = (s) => s === s.toUpperCase() && /[A-Z]/.test(s);
  if (isAllCaps(existing) && !isAllCaps(candidate)) return candidate;
  return existing;
};

// Distinct marker shapes per operator on the trend line, on top of the dash
// pattern, so two operators are never told apart by color alone.
const POINT_STYLES = ['circle', 'rect', 'triangle', 'rectRot', 'star', 'crossRot'];
const getOperatorPointStyle = (operatorIndex) => POINT_STYLES[operatorIndex % POINT_STYLES.length];

// Click hit-testing: 'index' mode guarantees SOME element is found for the
// clicked day even if the exact bar under the cursor has 0 height (a
// zero-value bar has no visible area to intersect). But 'index' mode
// returns every dataset at that day -- including every OTHER operator's
// bar, since operators are laid out as separate bars side-by-side within
// the same day's column, not as separate x-axis categories. Disambiguating
// by Y alone (ignoring X) is what was picking the wrong operator: two
// different operators' bars can easily be similar heights, so "closest in
// Y" doesn't mean "the one under the cursor" if it's actually offset
// horizontally. This first narrows to elements whose actual rendered shape
// (a bar's fill span, or a line point within a small tolerance) contains
// the click vertically, then breaks ties by nearest X -- so a click is
// always resolved against the specific column the cursor was over.
const pickClosestElement = (chart, nativeEvent, elements) => {
  if (!elements || elements.length === 0) return null;
  if (elements.length === 1) return elements[0];
  const rect = chart.canvas.getBoundingClientRect();
  const clickX = nativeEvent.clientX - rect.left;
  const clickY = nativeEvent.clientY - rect.top;

  const verticallyContains = [];
  elements.forEach(el => {
    const meta = chart.getDatasetMeta(el.datasetIndex);
    const point = meta && meta.data && meta.data[el.index];
    if (!point) return;
    if (typeof point.base === 'number') {
      // Bar element: base is the zero-edge, y is the far edge of this
      // specific stacked segment -- span between them is its fill area.
      const top = Math.min(point.y, point.base);
      const bottom = Math.max(point.y, point.base);
      if (clickY >= top - 2 && clickY <= bottom + 2) verticallyContains.push(el);
    } else if (typeof point.y === 'number' && Math.abs(point.y - clickY) <= 10) {
      verticallyContains.push(el); // line point within a small tolerance
    }
  });

  const candidates = verticallyContains.length > 0 ? verticallyContains : elements;
  let best = candidates[0];
  let bestDist = Infinity;
  candidates.forEach(el => {
    const meta = chart.getDatasetMeta(el.datasetIndex);
    const point = meta && meta.data && meta.data[el.index];
    if (!point || typeof point.x !== 'number') return;
    const dist = Math.abs(point.x - clickX);
    if (dist < bestDist) { bestDist = dist; best = el; }
  });
  return best;
};

// Custom plugin: shades every other "day" with a very light background band
// so consecutive days are easy to tell apart at a glance. Bands are passed
// in per-chart via options.plugins.dayBandPlugin.bands = [{startIndex,
// endIndex}], since the two day-wise charts have different category
// layouts (one category per day vs. one category per day+sub-section).
const DAY_BAND_PLUGIN = {
  id: 'dayBandPlugin',
  beforeDatasetsDraw(chart) {
    const opts = chart.options.plugins && chart.options.plugins.dayBandPlugin;
    const bands = opts && opts.bands;
    if (!bands || bands.length === 0) return;
    const { ctx, chartArea, scales } = chart;
    const xScale = scales.x;
    if (!xScale || !chartArea) return;
    const tickCount = (xScale.ticks && xScale.ticks.length) || 0;
    if (tickCount < 2) return;
    const step = xScale.getPixelForValue(1) - xScale.getPixelForValue(0);
    const half = step / 2;
    ctx.save();
    bands.forEach(({ startIndex, endIndex }) => {
      const left = xScale.getPixelForValue(startIndex) - half;
      const right = xScale.getPixelForValue(endIndex) + half;
      ctx.fillStyle = 'rgba(60,90,140,0.05)';
      ctx.fillRect(left, chartArea.top, right - left, chartArea.bottom - chartArea.top);
    });
    ctx.restore();
  }
};

// Custom plugin: writes each operator's name directly next to the end of
// their trend line, so identifying a line doesn't rely on hovering or
// cross-referencing dash patterns in the legend.
const LINE_END_LABEL_PLUGIN = {
  id: 'lineEndLabelPlugin',
  afterDatasetsDraw(chart) {
    const opts = chart.options.plugins && chart.options.plugins.lineEndLabelPlugin;
    const labels = opts && opts.labels;
    if (!labels || labels.length === 0) return;
    const { ctx, chartArea } = chart;
    ctx.save();
    ctx.font = '11px sans-serif';
    ctx.textBaseline = 'middle';
    labels.forEach(({ datasetIndex, text, color }) => {
      const meta = chart.getDatasetMeta(datasetIndex);
      if (!meta || !meta.data || meta.data.length === 0) return;
      const lastPoint = meta.data[meta.data.length - 1];
      if (!lastPoint) return;
      const x = Math.min(lastPoint.x + 6, chartArea.right - ctx.measureText(text).width - 4);
      const y = lastPoint.y;
      // small pill background so the label stays readable over bars/gridlines
      const textWidth = ctx.measureText(text).width;
      ctx.fillStyle = 'rgba(255,255,255,0.85)';
      ctx.fillRect(x - 2, y - 8, textWidth + 4, 16);
      ctx.fillStyle = color;
      ctx.fillText(text, x, y);
    });
    ctx.restore();
  }
};

// All 18 sections, fixed order, always shown (empty state if no data),
// selected by default.
const DEFAULT_SECTIONS = Object.keys(SECTION_META);
const DEFAULT_SELECTED_SECTIONS = [...DEFAULT_SECTIONS];

const FALLBACK_COLOR = '#6f42c1';

const getSectionColor = (section) => (SECTION_META[section] && SECTION_META[section].color) || FALLBACK_COLOR;
const getSectionGroup = (section) => (SECTION_META[section] && SECTION_META[section].group) || 'OTHER';
const getSectionIcon = (section) => {
  const group = getSectionGroup(section);
  return (GROUP_META[group] && GROUP_META[group].icon) || 'fa-industry';
};
const sectionHasPerformance = (section) => {
  const group = getSectionGroup(section);
  return !!(GROUP_META[group] && GROUP_META[group].hasPerformance);
};

// Helper to get Monday of the current week (local time)
const getMondayOfCurrentWeek = () => {
  const now = new Date();
  const day = now.getDay(); // 0 = Sunday, 1 = Monday, ... 6 = Saturday
  const diffToMonday = day === 0 ? 6 : day - 1; // Sunday -> go back 6 days
  return new Date(now.getFullYear(), now.getMonth(), now.getDate() - diffToMonday);
};

// Helper to get today's date with time zeroed out (local time)
const getToday = () => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate());
};

// Minimum date - only show data from 2024 onwards
const MIN_DATE = new Date(2024, 0, 1);
const MAX_DATE = new Date(2026, 11, 31);

const normalizeSection = (raw) => {
  if (!raw) return 'UNASSIGNED';
  return String(raw).trim().toUpperCase();
};

// Row is "missing production" if Produced is null/undefined/blank or 0 --
// used to flag rows in the detail table & modal with a red background.
const isMissingProduction = (record) => {
  const val = record && record.Produced;
  if (val === null || val === undefined || val === '') return true;
  const num = Number(val);
  return isNaN(num) || num === 0;
};

// ================= SEARCHABLE SELECT COMPONENT =================
const SearchableSelect = ({ options, value, onChange, placeholder, label }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredOptions = options.filter(option =>
    option.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
    option.value.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const selectedOption = options.find(opt => opt.value === value);

  return (
    <div className="searchable-select" ref={wrapperRef}>
      <div
        className="searchable-select-input"
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className={selectedOption ? '' : 'placeholder'}>
          {selectedOption ? selectedOption.label : placeholder}
        </span>
        <i className={`fas fa-chevron-${isOpen ? 'up' : 'down'}`}></i>
      </div>
      {isOpen && (
        <div className="searchable-select-dropdown">
          <input
            type="text"
            className="searchable-select-search"
            placeholder={`Search ${label}...`}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onClick={(e) => e.stopPropagation()}
            autoFocus
          />
          <div className="searchable-select-options">
            <div
              className="searchable-select-option"
              onClick={() => {
                onChange('');
                setIsOpen(false);
                setSearchTerm('');
              }}
            >
              All {label}s
            </div>
            {filteredOptions.map(option => (
              <div
                key={option.value}
                className={`searchable-select-option ${option.value === value ? 'selected' : ''}`}
                onClick={() => {
                  onChange(option.value);
                  setIsOpen(false);
                  setSearchTerm('');
                }}
              >
                {option.label}
              </div>
            ))}
            {filteredOptions.length === 0 && (
              <div className="searchable-select-no-results">No results found</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const ProductionReport = () => {
  // State
  const [data, setData] = useState([]);
  const [weeklyData, setWeeklyData] = useState({});
  // sectionsList is ALWAYS the fixed set of 18 -- it never shrinks just
  // because a section happened to have no rows in the current date range.
  const [sectionsList] = useState(DEFAULT_SECTIONS);
  const [selectedSections, setSelectedSections] = useState(DEFAULT_SELECTED_SECTIONS);
  const [stats, setStats] = useState([]);
  const [filterOptions, setFilterOptions] = useState({
    sections: DEFAULT_SECTIONS.map(s => ({ value: s, label: s })),
    customers: [],
    operators: [],
    workstations: [],
    years: [],
    weeks: [],
    parts: [],
    shifts: []
  });
  const [loading, setLoading] = useState(true);
  const [chartsLoading, setChartsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [visibleCount, setVisibleCount] = useState(30);
  const [hasMoreData, setHasMoreData] = useState(false);

  // Missing-data / completeness-check state
  const [missingData, setMissingData] = useState(null);
  const [missingDataLoading, setMissingDataLoading] = useState(true);

  // Day-detail modal (opens when a missing/zero/partial-zero date badge is
  // clicked, or when a bar/line point on a day-wise chart is clicked)
  const [dayModalData, setDayModalData] = useState({
    isOpen: false,
    section: '',
    date: '',
    issueType: '', // 'missing' | 'zero' | 'partial' | 'operator-bar' | 'target-chart' | 'operator-line'
    operator: '', // set when the click came from a specific operator's bar/line
    records: [],
    loading: false
  });

  // Day-wise breakdown data (multi-color per-operator charts for SANDING
  // RIGID / DRILL TAP RIGID / TRIMMING FLEX / ASSEMBLY, and day-wise
  // Target-vs-Produced-by-subsection charts for RIGID/FLEX MOLDING).
  const [dailyOperatorData, setDailyOperatorData] = useState({});
  const [dailyTargetData, setDailyTargetData] = useState({});
  const [dailyBreakdownLoading, setDailyBreakdownLoading] = useState(true);

  // Print
  const [printRequested, setPrintRequested] = useState(false);

  // Both filter panels start collapsed
  const [showFilters, setShowFilters] = useState(true);
  const [showSectionFilter, setShowSectionFilter] = useState(false);

  // Modal state
  const [modalData, setModalData] = useState({
    isOpen: false,
    section: '',
    week: '',
    year: '',
    records: [],
    loading: false
  });

  const [filters, setFilters] = useState({
    startDate: getMondayOfCurrentWeek(),
    endDate: getToday(),
    year: '',
    section: '',
    customer: '',
    operator: '',
    workstation: '',
    week: '',
    part: '',
    shift: ''
  });

  const token = window.localStorage.getItem('acessToken');

  // Dynamic chart refs
  const chartRefs = useRef({});
  // Refs for the day-wise group charts (one canvas per group, not per section)
  const dailyChartRefs = useRef({});

  // Load filter options on mount
  useEffect(() => {
    loadFilterOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load data when filters change
  useEffect(() => {
    loadData();
    loadWeeklySummary();
    loadStats();
    loadMissingData();
    loadDailyBreakdown();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  // NOTE: the old per-section weekly chart canvases (chart-<section>) were
  // removed from the page in favor of the day-wise group charts below, so
  // the effect that used to build them here was removed too -- it would
  // otherwise retry forever looking for canvases that no longer render.

  // Build/rebuild the day-wise charts whenever their underlying data changes
  useEffect(() => {
    if (dailyBreakdownLoading) return;

    Object.keys(dailyChartRefs.current).forEach(key => {
      if (dailyChartRefs.current[key]) {
        dailyChartRefs.current[key].destroy();
        dailyChartRefs.current[key] = null;
      }
    });

    OPERATOR_DAYWISE_GROUPS.forEach(group => {
      const groupData = dailyOperatorData[group];
      if (groupData && groupData.days.length > 0 && selectedSections.some(s => getSectionGroup(s) === group)) {
        updateDailyOperatorChart(group, groupData);
      }
    });

    TARGET_DAYWISE_GROUPS.forEach(group => {
      const groupData = dailyTargetData[group];
      if (groupData && groupData.days.length > 0 && selectedSections.some(s => getSectionGroup(s) === group)) {
        updateDailyTargetChart(group, groupData);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dailyOperatorData, dailyTargetData, dailyBreakdownLoading, selectedSections]);

  const buildQueryString = (extra = {}) => {
    const startDate = filters.startDate.toISOString().split('T')[0];
    const endDate = filters.endDate.toISOString().split('T')[0];
    const params = new URLSearchParams({ token, startDate, endDate, ...extra });
    if (filters.year) params.set('year', filters.year);
    if (filters.section) params.set('section', filters.section);
    if (filters.customer) params.set('customer', filters.customer);
    if (filters.operator) params.set('operator', filters.operator);
    if (filters.workstation) params.set('workstation', filters.workstation);
    if (filters.part) params.set('part', filters.part);
    if (filters.shift) params.set('shift', filters.shift);
    if (filters.week) params.set('week', filters.week);
    return params.toString();
  };

  const loadFilterOptions = async () => {
    try {
      const startDate = filters.startDate.toISOString().split('T')[0];
      const endDate = filters.endDate.toISOString().split('T')[0];
      const response = await fetch(`${API_BASE}/filter-options?token=${token}&startDate=${startDate}&endDate=${endDate}`);
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();

      const formatOptions = (items) => items.map(item => ({ value: item, label: String(item) }));

      const weekOptions = (data.weeks || []).map(w => ({
        value: w.week,
        label: w.label || `Week ${w.week} (${w.year})`
      }));

      const yearOptions = (data.years || []).map(year => ({
        value: year,
        label: String(year)
      }));

      setFilterOptions({
        sections: formatOptions(data.sections && data.sections.length ? data.sections : DEFAULT_SECTIONS),
        customers: formatOptions(data.customers || []),
        operators: formatOptions(data.operators || []),
        workstations: formatOptions(data.workstations || []),
        years: yearOptions,
        weeks: weekOptions,
        parts: formatOptions(data.parts || []),
        shifts: formatOptions(data.shifts || [])
      });
    } catch (error) {
      console.error('Error loading filter options:', error);
      setError('Failed to load filter options');
      // Keep the hard-coded section list so the dropdown still works
      setFilterOptions(prev => ({
        ...prev,
        sections: DEFAULT_SECTIONS.map(s => ({ value: s, label: s }))
      }));
    }
  };

  const loadData = async () => {
    setLoading(true);
    setError(null);
    setData([]);
    setVisibleCount(30);

    try {
      let qs = buildQueryString({ limit: 200 });
      const url = `${API_BASE}/production?${qs}`;

      const response = await fetch(url);
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      const result = await response.json();
      const dataArray = Array.isArray(result) ? result : [];

      const limitedData = dataArray.slice(0, 200);
      setData(limitedData);
      setHasMoreData(dataArray.length > 200);
    } catch (error) {
      console.error('Error loading data:', error);
      setError('Failed to load production data: ' + error.message);
      setData([]);
    }
    setLoading(false);
  };

  const loadWeeklySummary = async () => {
    setChartsLoading(true);
    try {
      const qs = buildQueryString();
      const response = await fetch(`${API_BASE}/weekly-summary?${qs}`);
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const result = await response.json();
      processWeeklySummary(Array.isArray(result) ? result : []);
    } catch (error) {
      console.error('Error loading weekly summary:', error);
      setWeeklyData({});
      setChartsLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      let qs = buildQueryString();
      const response = await fetch(`${API_BASE}/stats?${qs}`);
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const result = await response.json();
      setStats(Array.isArray(result) ? result : []);
    } catch (error) {
      console.error('Error loading stats:', error);
      setStats([]);
    }
  };

  // Scans every weekday (Mon-Fri) in the selected date range, per section
  // (SEATS/BODY/TACKING STRIP individually -- not the TOTAL rollup, which
  // would mask a bad day behind a good one), and reports which days have
  // no data at all, ALL entries at 0 produced, or SOME entries at 0
  // produced (a partial failure a daily-sum check would otherwise miss).
  const loadMissingData = async () => {
    setMissingDataLoading(true);
    try {
      const startDate = filters.startDate.toISOString().split('T')[0];
      const endDate = filters.endDate.toISOString().split('T')[0];
      const response = await fetch(`${API_BASE}/missing-data?token=${token}&startDate=${startDate}&endDate=${endDate}`);
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const result = await response.json();
      setMissingData(result);
    } catch (error) {
      console.error('Error loading missing data report:', error);
      setMissingData(null);
    }
    setMissingDataLoading(false);
  };

  // Fetches raw production records (using the existing /production endpoint,
  // same one the modals use) for every relevant sub-section, then bucket
  // them client-side by day. This powers two different day-wise chart types:
  //
  //   1) OPERATOR_DAYWISE_GROUPS (Sanding Rigid / Drill Tap Rigid /
  //      Trimming Flex / Assembly) -- bucketed by day + operator, so each
  //      operator gets their own colored, stacked bar per day (stacked by
  //      Seat/Body/Tacking Strip where the group has those sub-parts), plus
  //      a running-total line per operator to show trend across the range.
  //
  //   2) TARGET_DAYWISE_GROUPS (Rigid Molding / Flex Molding) -- bucketed by
  //      day + sub-section (Seat/Body/Tacking Strip), Target vs Produced,
  //      no operator split.
  //
  // Uses a high limit per request since this needs the FULL date range, not
  // the 200-row page used by the main table.
  const loadDailyBreakdown = async () => {
    setDailyBreakdownLoading(true);
    try {
      const startDate = filters.startDate.toISOString().split('T')[0];
      const endDate = filters.endDate.toISOString().split('T')[0];

      const fetchSectionRecords = async (section) => {
        const params = new URLSearchParams({ token, startDate, endDate, section, limit: '5000' });
        if (filters.customer) params.set('customer', filters.customer);
        if (filters.operator) params.set('operator', filters.operator);
        if (filters.workstation) params.set('workstation', filters.workstation);
        if (filters.part) params.set('part', filters.part);
        if (filters.shift) params.set('shift', filters.shift);
        const res = await fetch(`${API_BASE}/production?${params.toString()}`);
        if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
        const json = await res.json();
        return Array.isArray(json) ? json : [];
      };

      // ---------------- Operator-colored groups (Produced only) ----------------
      const operatorResults = {};
      for (const group of OPERATOR_DAYWISE_GROUPS) {
        // Prefer the individual sub-sections (SEATS/BODY/TACKING STRIP) over
        // the TOTAL rollup so we can stack by sub-part; fall back to
        // whatever sections exist for the group (e.g. Assembly only has a
        // TOTAL section).
        const nonTotalSections = sectionsList.filter(s => getSectionGroup(s) === group && !s.endsWith('- TOTAL'));
        const sectionsToFetch = nonTotalSections.length > 0
          ? nonTotalSections
          : sectionsList.filter(s => getSectionGroup(s) === group);

        const recordsBySection = await Promise.all(sectionsToFetch.map(fetchSectionRecords));

        const byDay = {};
        const operatorSet = new Set();
        const operatorDisplayNames = {};
        const subLabels = sectionsToFetch.map(s => s.replace(`${group} - `, ''));

        sectionsToFetch.forEach((section, idx) => {
          const subLabel = subLabels[idx];
          (recordsBySection[idx] || []).forEach(record => {
            const date = record.FormattedDate;
            if (!date) return;
            const rawOperator = String(record.Operator || 'Unassigned').trim();
            const operator = normalizeOperatorKey(rawOperator); // grouping key
            const produced = Number(record.Produced) || 0;
            const scrap = Number(record.Scrap) || 0;

            operatorSet.add(operator);
            operatorDisplayNames[operator] = pickBetterDisplayName(operatorDisplayNames[operator], rawOperator);
            if (!byDay[date]) byDay[date] = {};
            if (!byDay[date][operator]) byDay[date][operator] = { total: 0, totalScrap: 0, bySub: {} };
            if (!byDay[date][operator].bySub[subLabel]) byDay[date][operator].bySub[subLabel] = 0;

            byDay[date][operator].bySub[subLabel] += produced;
            byDay[date][operator].total += produced;
            byDay[date][operator].totalScrap += scrap;
          });
        });

        const days = Object.keys(byDay).sort();
        operatorResults[group] = {
          days,
          operators: Array.from(operatorSet).sort(),
          displayNames: operatorDisplayNames, // normalized key -> best display name
          subLabels,
          byDay
        };
      }

      // ---------------- Target-based groups (Target vs Produced) ----------------
      const targetResults = {};
      for (const group of TARGET_DAYWISE_GROUPS) {
        const sections = sectionsList.filter(s => getSectionGroup(s) === group && !s.endsWith('- TOTAL'));
        const recordsBySection = await Promise.all(sections.map(fetchSectionRecords));

        const byDay = {};
        const subLabels = sections.map(s => s.replace(`${group} - `, ''));

        sections.forEach((section, idx) => {
          const subLabel = subLabels[idx];
          (recordsBySection[idx] || []).forEach(record => {
            const date = record.FormattedDate;
            if (!date) return;
            const target = Number(record.Target) || 0;
            const produced = Number(record.Produced) || 0;

            if (!byDay[date]) byDay[date] = {};
            if (!byDay[date][subLabel]) byDay[date][subLabel] = { target: 0, produced: 0 };
            byDay[date][subLabel].target += target;
            byDay[date][subLabel].produced += produced;
          });
        });

        const days = Object.keys(byDay).sort();
        targetResults[group] = { days, subLabels, byDay };
      }

      setDailyOperatorData(operatorResults);
      setDailyTargetData(targetResults);
    } catch (error) {
      console.error('Error loading daily breakdown:', error);
      setDailyOperatorData({});
      setDailyTargetData({});
    }
    setDailyBreakdownLoading(false);
  };

  // Fetch detailed data for a specific week and section
  const fetchWeekDetails = async (section, year, week) => {
    setModalData(prev => ({ ...prev, loading: true }));

    try {
      const startDate = filters.startDate.toISOString().split('T')[0];
      const endDate = filters.endDate.toISOString().split('T')[0];

      let url = `${API_BASE}/production?token=${token}&startDate=${startDate}&endDate=${endDate}&section=${encodeURIComponent(section)}&week=${week}&year=${year}&limit=1000`;
      if (filters.customer) url += `&customer=${encodeURIComponent(filters.customer)}`;
      if (filters.operator) url += `&operator=${encodeURIComponent(filters.operator)}`;
      if (filters.workstation) url += `&workstation=${encodeURIComponent(filters.workstation)}`;
      if (filters.part) url += `&part=${encodeURIComponent(filters.part)}`;
      if (filters.shift) url += `&shift=${encodeURIComponent(filters.shift)}`;

      const response = await fetch(url);
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      const result = await response.json();
      const records = Array.isArray(result) ? result : [];

      setModalData({
        isOpen: true,
        section,
        week,
        year,
        records,
        loading: false
      });
    } catch (error) {
      console.error('Error fetching week details:', error);
      setModalData(prev => ({
        ...prev,
        loading: false,
        records: []
      }));
    }
  };

  // Always-fresh ref to fetchWeekDetails
  const fetchWeekDetailsRef = useRef(fetchWeekDetails);
  useEffect(() => {
    fetchWeekDetailsRef.current = fetchWeekDetails;
  });

  const closeModal = () => {
    setModalData({
      isOpen: false,
      section: '',
      week: '',
      year: '',
      records: [],
      loading: false
    });
  };

  // Opens the day-detail modal for a clicked date badge in the Data
  // Completeness Check section, or for a clicked bar in a day-wise chart.
  // Queries that EXACT section (e.g. "RIGID MOLDING - SEATS") for the
  // single day -- and, when operator is given, for that operator only --
  // so the modal shows exactly what's behind the bar/point that was
  // clicked, nothing from any other bar/line on the same day.
  const fetchDayDetails = async (section, date, issueType, operator = '') => {
    setDayModalData({ isOpen: true, section, date, issueType, operator, records: [], loading: true });
    try {
      let url = `${API_BASE}/production?token=${token}&startDate=${date}&endDate=${date}&section=${encodeURIComponent(section)}&limit=1000`;
      if (operator) url += `&operator=${encodeURIComponent(operator)}`;
      const response = await fetch(url);
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const result = await response.json();
      const records = Array.isArray(result) ? result : [];
      setDayModalData(prev => ({ ...prev, records, loading: false }));
    } catch (error) {
      console.error('Error fetching day details:', error);
      setDayModalData(prev => ({ ...prev, records: [], loading: false }));
    }
  };

  // Opens the day-detail modal for a clicked point on an operator's
  // cumulative trend line. A trend-line point represents that operator's
  // work across ALL of the group's sub-sections for that one day, so this
  // fetches each sub-section filtered to that operator and merges the
  // results -- still scoped to exactly that operator + that day, nothing
  // from any other operator.
  const fetchOperatorDayDetails = async (group, subLabels, date, operator) => {
    setDayModalData({ isOpen: true, section: group, date, issueType: 'operator-line', operator, records: [], loading: true });
    try {
      const sections = subLabels.map(sl => `${group} - ${sl}`);
      const resultsBySection = await Promise.all(sections.map(async (section) => {
        const url = `${API_BASE}/production?token=${token}&startDate=${date}&endDate=${date}&section=${encodeURIComponent(section)}&operator=${encodeURIComponent(operator)}&limit=1000`;
        const res = await fetch(url);
        if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
        const json = await res.json();
        return Array.isArray(json) ? json : [];
      }));
      const records = resultsBySection.flat();
      setDayModalData(prev => ({ ...prev, records, loading: false }));
    } catch (error) {
      console.error('Error fetching operator day details:', error);
      setDayModalData(prev => ({ ...prev, records: [], loading: false }));
    }
  };

  const closeDayModal = () => {
    setDayModalData({ isOpen: false, section: '', date: '', issueType: '', operator: '', records: [], loading: false });
  };

  // Just re-buckets whatever weekly rows came back by Section. It does NOT
  // touch sectionsList -- that always stays the fixed 18, so a section with
  // zero rows in this date range still renders its card (with an empty
  // state) instead of disappearing.
  const processWeeklySummary = useCallback((rows) => {
    const weekly = {};

    (rows || []).forEach(row => {
      const section = normalizeSection(row.Section);
      if (!weekly[section]) weekly[section] = {};

      const year = row.YearNumber || 0;
      const week = row.WeekNumber || 0;
      const key = `${year}-${String(week).padStart(2, '0')}`;

      weekly[section][key] = {
        year,
        week,
        weekStart: row.WeekStartDate,
        target: Number(row.Target) || 0,
        produced: Number(row.Produced) || 0,
        scrap: Number(row.Scrap) || 0,
        records: Number(row.RecordCount) || 0
      };
    });

    setWeeklyData(weekly);
    setChartsLoading(false);
  }, []);

  const updateChart = useCallback((section, weeklyRows) => {
    const canvasId = `chart-${section.replace(/[^a-zA-Z0-9]/g, '-')}`;
    const canvas = document.getElementById(canvasId);
    if (!canvas) {
      // Canvas might not be rendered yet, retry after a short delay
      setTimeout(() => {
        const retryCanvas = document.getElementById(canvasId);
        if (retryCanvas) {
          updateChart(section, weeklyRows);
        }
      }, 100);
      return;
    }

    const ctx = canvas.getContext('2d');

    const sortedData = [...weeklyRows].sort((a, b) => (a.year - b.year) || (a.week - b.week));
    const maxDataPoints = 16;
    const displayData = sortedData.length > maxDataPoints
      ? sortedData.slice(-maxDataPoints)
      : sortedData;

    const labels = displayData.map(w => `Wk ${w.week} '${String(w.year).slice(-2)}`);
    const produced = displayData.map(w => w.produced);
    const color = getSectionColor(section);
    const hasPerformance = sectionHasPerformance(section);

    // Destroy existing chart for this section if it exists
    if (chartRefs.current[section]) {
      chartRefs.current[section].destroy();
      chartRefs.current[section] = null;
    }

    let datasets;
    let scales;

    if (hasPerformance) {
      // Rigid Molding / Flex Molding: Target vs Produced + Efficiency %
      const target = displayData.map(w => w.target);
      const efficiency = displayData.map(w => w.target > 0 ? Math.round((w.produced / w.target) * 1000) / 10 : 0);
      const maxVal = Math.max(1, ...target, ...produced);

      datasets = [
        {
          label: 'Target',
          data: target,
          backgroundColor: `${color}33`,
          borderColor: color,
          borderWidth: 1.5,
          borderRadius: 4,
          yAxisID: 'y'
        },
        {
          label: 'Produced',
          data: produced,
          backgroundColor: color,
          borderColor: color,
          borderWidth: 1,
          borderRadius: 4,
          yAxisID: 'y'
        },
        {
          label: 'Efficiency %',
          data: efficiency,
          type: 'line',
          borderColor: '#ff6b35',
          backgroundColor: 'rgba(255, 107, 53, 0.12)',
          fill: true,
          tension: 0.35,
          yAxisID: 'y1',
          pointRadius: 3,
          pointBackgroundColor: '#ff6b35',
          borderWidth: 2
        }
      ];

      scales = {
        y: {
          beginAtZero: true,
          position: 'left',
          suggestedMax: Math.ceil(maxVal * 1.15),
          grid: { display: true, color: 'rgba(0,0,0,0.05)' },
          title: { display: true, text: 'Units', font: { size: 10 } }
        },
        y1: {
          beginAtZero: true,
          position: 'right',
          grid: { display: false },
          max: 100,
          ticks: { callback: (value) => value + '%' }
        }
      };
    } else {
      // Sanding Rigid / Drill Tap Rigid / Trimming Flex / Assembly:
      // no predefined target -- just Produced (and Scrap) counts.
      const scrap = displayData.map(w => w.scrap);
      const maxVal = Math.max(1, ...produced, ...scrap);

      datasets = [
        {
          label: 'Produced',
          data: produced,
          backgroundColor: color,
          borderColor: color,
          borderWidth: 1,
          borderRadius: 4,
          yAxisID: 'y'
        },
        {
          label: 'Scrap',
          data: scrap,
          backgroundColor: 'rgba(150,150,150,0.35)',
          borderColor: '#999',
          borderWidth: 1,
          borderRadius: 4,
          yAxisID: 'y'
        }
      ];

      scales = {
        y: {
          beginAtZero: true,
          position: 'left',
          suggestedMax: Math.ceil(maxVal * 1.15),
          grid: { display: true, color: 'rgba(0,0,0,0.05)' },
          title: { display: true, text: 'Units Produced', font: { size: 10 } }
        }
      };
    }

    // Create new chart
    const newChart = new Chart(ctx, {
      type: 'bar',
      data: { labels, datasets },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        animation: { duration: 300 },
        interaction: { mode: 'index', intersect: false },
        onClick: (event, elements) => {
          if (elements && elements.length > 0) {
            const index = elements[0].index;
            const dataPoint = displayData[index];
            if (dataPoint) {
              fetchWeekDetailsRef.current(section, dataPoint.year, dataPoint.week);
            }
          }
        },
        plugins: {
          legend: {
            position: 'top',
            labels: { boxWidth: 10, padding: 8, font: { size: 10 } }
          },
          tooltip: {
            callbacks: {
              label: (context) => {
                if (context.dataset.label === 'Efficiency %') {
                  return `Efficiency: ${context.parsed.y}%`;
                }
                return `${context.dataset.label}: ${context.parsed.y}`;
              },
              afterBody: (tooltipItems) => {
                if (tooltipItems && tooltipItems.length > 0) {
                  const index = tooltipItems[0].dataIndex;
                  const dataPoint = displayData[index];
                  if (dataPoint) {
                    return 'Click to view details for this week';
                  }
                }
                return '';
              }
            }
          }
        },
        scales
      }
    });

    chartRefs.current[section] = newChart;
  }, []);

  // Multi-part, day-wise, per-operator chart for SANDING RIGID / DRILL TAP
  // RIGID / TRIMMING FLEX / ASSEMBLY. Color is by BODY PART, not operator --
  // Seat is always light, Body is always dark, Tacking Strip a mid tone --
  // so color never implies "this operator". Operators are told apart by
  // their bar's position (grouped per day), and on the trend line by dash
  // pattern + marker shape + an on-chart name label (not by color). Hover
  // shows only the single hovered bar/point. Clicking anywhere in a day's
  // column opens the detail popup scoped to exactly that operator +
  // sub-part + day (bar) or that operator + day (line point) -- click
  // detection uses the whole day's column so it still works even when the
  // exact bar under the cursor has zero height. The chart scrolls
  // horizontally instead of compressing every day into one screen.
  const updateDailyOperatorChart = useCallback((group, groupData) => {
    const canvasId = `daily-op-chart-${group.replace(/[^a-zA-Z0-9]/g, '-')}`;
    const wrapId = `daily-op-chart-wrap-${group.replace(/[^a-zA-Z0-9]/g, '-')}`;
    const canvas = document.getElementById(canvasId);
    const wrapper = document.getElementById(wrapId);
    if (!canvas || !wrapper) {
      setTimeout(() => {
        const retryCanvas = document.getElementById(canvasId);
        const retryWrap = document.getElementById(wrapId);
        if (retryCanvas && retryWrap) updateDailyOperatorChart(group, groupData);
      }, 100);
      return;
    }
    const ctx = canvas.getContext('2d');

    if (dailyChartRefs.current[group]) {
      dailyChartRefs.current[group].destroy();
      dailyChartRefs.current[group] = null;
    }

    const { days, operators, subLabels, byDay, displayNames } = groupData;
    const displayDays = days; // show every day -- the chart scrolls instead of compressing
    const labels = displayDays.map(d => d.slice(5)); // MM-DD
    const nameFor = (op) => (displayNames && displayNames[op]) || op;

    // Make the chart wide enough that each day gets a readable amount of
    // space, and let the outer container scroll horizontally to it.
    const perDayWidth = Math.max(70, operators.length * 46);
    wrapper.style.width = `${Math.max(2200, displayDays.length * perDayWidth)}px`;
    wrapper.style.height = '100%';

    const barDatasets = [];
    operators.forEach(operator => {
      subLabels.forEach((subLabel) => {
        const color = getSubpartBarColor(subLabel);
        barDatasets.push({
          type: 'bar',
          label: `${nameFor(operator)} - ${subLabel}`,
          stack: operator,
          data: displayDays.map(d => {
            const entry = byDay[d] && byDay[d][operator];
            return entry ? (entry.bySub[subLabel] || 0) : 0;
          }),
          backgroundColor: color,
          borderColor: color,
          borderWidth: 1,
          borderRadius: 3,
          yAxisID: 'y'
        });
      });
    });

    // Cumulative Produced per operator, as a trend line over the same days.
    // A single neutral line color is used for all operators -- they are
    // told apart by dash pattern, marker shape, and an on-chart name label.
    const lineColor = (GROUP_META[group] || {}).color || '#333';
    const lineDatasets = operators.map((operator, opIdx) => {
      let running = 0;
      const points = displayDays.map(d => {
        const entry = byDay[d] && byDay[d][operator];
        running += entry ? entry.total : 0;
        return running;
      });
      return {
        type: 'line',
        label: `${nameFor(operator)} (cumulative)`,
        data: points,
        borderColor: lineColor,
        backgroundColor: 'transparent',
        borderWidth: 2.5,
        borderDash: getOperatorDash(opIdx),
        tension: 0.3,
        pointRadius: 4,
        pointStyle: getOperatorPointStyle(opIdx),
        pointBackgroundColor: lineColor,
        yAxisID: 'y1'
      };
    });

    const maxBarVal = Math.max(1, ...operators.map(op =>
      Math.max(0, ...displayDays.map(d => (byDay[d] && byDay[d][op]) ? byDay[d][op].total : 0))
    ));
    const maxLineVal = Math.max(1, ...lineDatasets.map(ds => Math.max(0, ...ds.data)));

    // Alternate a light background band behind every other day so
    // consecutive days are easy to tell apart at a glance.
    const dayBands = displayDays
      .map((_, i) => ({ startIndex: i, endIndex: i }))
      .filter((_, i) => i % 2 === 1);

    // Name labels drawn at the end of each trend line
    const lineEndLabels = operators.map((operator, opIdx) => ({
      datasetIndex: barDatasets.length + opIdx,
      text: nameFor(operator),
      color: lineColor
    }));

    const newChart = new Chart(ctx, {
      data: { labels, datasets: [...barDatasets, ...lineDatasets] },
      plugins: [DAY_BAND_PLUGIN, LINE_END_LABEL_PLUGIN],
      options: {
        responsive: true,
        maintainAspectRatio: false,
        animation: { duration: 300 },
        layout: { padding: { right: 90 } }, // room for the trend-line name labels
        // Hover shows only the single nearest item's line/bar.
        interaction: { mode: 'nearest', intersect: true },
        onClick: (evt, elements, chart) => {
          // 'index' hit-testing so a click anywhere in a day's column
          // registers, even over a bar with 0 height; then pick whichever
          // element's actual rendered position is closest to the click.
          const indexElements = chart.getElementsAtEventForMode(evt.native || evt, 'index', { intersect: false }, true);
          const picked = pickClosestElement(chart, evt.native || evt, indexElements);
          if (!picked) return;
          const { datasetIndex, index } = picked;
          const day = displayDays[index];
          if (!day) return;

          if (datasetIndex < barDatasets.length) {
            // Bar click: exact operator + sub-part + day
            const operatorIndex = Math.floor(datasetIndex / subLabels.length);
            const subIndex = datasetIndex % subLabels.length;
            const operator = operators[operatorIndex];
            const subLabel = subLabels[subIndex];
            fetchDayDetails(`${group} - ${subLabel}`, day, 'operator-bar', nameFor(operator));
          } else {
            // Line point click: exact operator + day, across that
            // operator's sub-parts for the group
            const lineIndex = datasetIndex - barDatasets.length;
            const operator = operators[lineIndex];
            fetchOperatorDayDetails(group, subLabels, day, nameFor(operator));
          }
        },
        plugins: {
          dayBandPlugin: { bands: dayBands },
          lineEndLabelPlugin: { labels: lineEndLabels },
          legend: {
            position: 'bottom',
            labels: {
              boxWidth: 14,
              padding: 10,
              font: { size: 13 },
              // Collapse the legend to one entry per operator (hide the
              // per-sub-part bar duplicates) -- the trend line entry per
              // operator (with its dash pattern) is the one shown.
              filter: (item) => !item.text.includes(' - ') || subLabels.length === 1
            }
          },
          tooltip: {
            titleFont: { size: 13 },
            bodyFont: { size: 13 },
            callbacks: {
              // Only the single hovered item's line -- not the whole day
              label: (context) => `${context.dataset.label}: ${context.parsed.y}`,
              title: (items) => (items && items[0] ? `${group} -- ${items[0].label}` : '')
            }
          }
        },
        scales: {
          y: {
            beginAtZero: true,
            position: 'left',
            stacked: true,
            suggestedMax: Math.ceil(maxBarVal * 1.2),
            grid: { display: true, color: 'rgba(0,0,0,0.05)' },
            title: { display: true, text: 'Units Produced / Day', font: { size: 13 } },
            ticks: { font: { size: 12 } }
          },
          y1: {
            beginAtZero: true,
            position: 'right',
            grid: { display: false },
            suggestedMax: Math.ceil(maxLineVal * 1.15),
            title: { display: true, text: 'Cumulative', font: { size: 13 } },
            ticks: { font: { size: 12 } }
          },
          x: { stacked: true, ticks: { font: { size: 12 } } }
        }
      }
    });

    dailyChartRefs.current[group] = newChart;
  }, []);

  // Day-wise Target vs Produced chart for RIGID MOLDING / FLEX MOLDING,
  // broken out by sub-section (Seat / Body / Tacking Strip). Target is drawn
  // as a light, wide bar in the BACKGROUND and Produced as a dark, narrower
  // bar OVERLAPPING it in the same slot (not two side-by-side bars) --
  // `grouped: false` on both datasets makes them share the same x position
  // instead of getting their own slots. Categories are flattened to
  // day+sub-section pairs (e.g. "08-20 Seat", "08-20 Body", ...) so the
  // sub-sections still read as a cluster per day, with a light background
  // band behind every other day. The chart scrolls horizontally instead of
  // compressing every day onto one screen, and clicking anywhere in a
  // day+sub-section's column opens that exact combination's detail popup.
  const updateDailyTargetChart = useCallback((group, groupData) => {
    const canvasId = `daily-target-chart-${group.replace(/[^a-zA-Z0-9]/g, '-')}`;
    const wrapId = `daily-target-chart-wrap-${group.replace(/[^a-zA-Z0-9]/g, '-')}`;
    const canvas = document.getElementById(canvasId);
    const wrapper = document.getElementById(wrapId);
    if (!canvas || !wrapper) {
      setTimeout(() => {
        const retryCanvas = document.getElementById(canvasId);
        const retryWrap = document.getElementById(wrapId);
        if (retryCanvas && retryWrap) updateDailyTargetChart(group, groupData);
      }, 100);
      return;
    }
    const ctx = canvas.getContext('2d');

    if (dailyChartRefs.current[group]) {
      dailyChartRefs.current[group].destroy();
      dailyChartRefs.current[group] = null;
    }

    const { days, subLabels, byDay } = groupData;
    const displayDays = days; // show every day -- the chart scrolls instead of compressing

    const subAbbrev = (s) => (s === 'SEATS' ? 'St' : s === 'BODY' ? 'Bd' : s === 'TACKING STRIP' ? 'Tk' : s.slice(0, 2));

    // Flatten (day, subLabel) into one combined category axis
    const comboMeta = [];
    const labels = [];
    displayDays.forEach(day => {
      subLabels.forEach(subLabel => {
        comboMeta.push({ day, subLabel });
        labels.push(`${day.slice(5)} ${subAbbrev(subLabel)}`);
      });
    });

    // Wide enough that each day+sub-section combo gets a readable slot
    const perComboWidth = 46;
    wrapper.style.width = `${Math.max(2200, displayDays.length * perComboWidth)}px`;
    wrapper.style.height = '100%';

    const targetData = comboMeta.map(({ day, subLabel }) => (byDay[day] && byDay[day][subLabel]) ? byDay[day][subLabel].target : 0);
    const producedData = comboMeta.map(({ day, subLabel }) => (byDay[day] && byDay[day][subLabel]) ? byDay[day][subLabel].produced : 0);
    const bgTarget = comboMeta.map(({ subLabel }) => subpartColor(subLabel, 'target'));
    const bgProduced = comboMeta.map(({ subLabel }) => subpartColor(subLabel, 'produced'));

    const datasets = [
      {
        type: 'bar',
        label: 'Target',
        data: targetData,
        backgroundColor: bgTarget,
        borderColor: bgProduced,
        borderWidth: 1,
        borderRadius: 3,
        grouped: false,
        barPercentage: 0.9,
        categoryPercentage: 0.82,
        yAxisID: 'y'
      },
      {
        type: 'bar',
        label: 'Produced',
        data: producedData,
        backgroundColor: bgProduced,
        borderColor: bgProduced,
        borderWidth: 1,
        borderRadius: 3,
        grouped: false,
        barPercentage: 0.5,
        categoryPercentage: 0.82,
        yAxisID: 'y'
      }
    ];

    const maxVal = Math.max(1, ...targetData, ...producedData);

    // Alternate a light background band behind every other day (each day
    // spans subLabels.length consecutive combo categories)
    const dayBands = displayDays
      .map((_, dayIdx) => ({ startIndex: dayIdx * subLabels.length, endIndex: dayIdx * subLabels.length + subLabels.length - 1 }))
      .filter((_, dayIdx) => dayIdx % 2 === 1);

    const newChart = new Chart(ctx, {
      data: { labels, datasets },
      plugins: [DAY_BAND_PLUGIN],
      options: {
        responsive: true,
        maintainAspectRatio: false,
        animation: { duration: 300 },
        // Hover shows only the single nearest dataset's value.
        interaction: { mode: 'nearest', intersect: true },
        onClick: (evt, elements, chart) => {
          // 'index' hit-testing so a click anywhere in a day+sub-section's
          // column registers, even over a bar with 0 height.
          const indexElements = chart.getElementsAtEventForMode(evt.native || evt, 'index', { intersect: false }, true);
          const picked = pickClosestElement(chart, evt.native || evt, indexElements);
          if (!picked) return;
          const combo = comboMeta[picked.index];
          if (!combo) return;
          fetchDayDetails(`${group} - ${combo.subLabel}`, combo.day, 'target-chart');
        },
        plugins: {
          dayBandPlugin: { bands: dayBands },
          legend: {
            position: 'bottom',
            labels: { boxWidth: 14, padding: 10, font: { size: 13 } }
          },
          tooltip: {
            titleFont: { size: 13 },
            bodyFont: { size: 13 },
            callbacks: {
              title: (items) => {
                if (!items || !items[0]) return '';
                const combo = comboMeta[items[0].dataIndex];
                return combo ? `${combo.subLabel} -- ${combo.day}` : '';
              },
              // Only the single hovered dataset's value, not Target+Produced together
              label: (context) => `${context.dataset.label}: ${context.parsed.y}`
            }
          }
        },
        scales: {
          y: {
            beginAtZero: true,
            suggestedMax: Math.ceil(maxVal * 1.15),
            grid: { display: true, color: 'rgba(0,0,0,0.05)' },
            title: { display: true, text: 'Units', font: { size: 13 } },
            ticks: { font: { size: 12 } }
          },
          x: {
            ticks: { font: { size: 11 }, maxRotation: 60, minRotation: 45 }
          }
        }
      }
    });

    dailyChartRefs.current[group] = newChart;
  }, []);

  const handleSectionToggle = (section) => {
    setSelectedSections(prev => {
      if (prev.includes(section)) {
        return prev.filter(s => s !== section);
      } else {
        return [...prev, section];
      }
    });
  };

  const selectAllSections = () => {
    setSelectedSections([...sectionsList]);
  };

  const deselectAllSections = () => {
    setSelectedSections([]);
  };

  const selectGroupSections = (group) => {
    const groupSections = sectionsList.filter(s => getSectionGroup(s) === group);
    setSelectedSections(prev => Array.from(new Set([...prev, ...groupSections])));
  };

  const deselectGroupSections = (group) => {
    setSelectedSections(prev => prev.filter(s => getSectionGroup(s) !== group));
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const resetFilters = () => {
    setFilters({
      startDate: getMondayOfCurrentWeek(),
      endDate: getToday(),
      year: '',
      section: '',
      customer: '',
      operator: '',
      workstation: '',
      week: '',
      part: '',
      shift: ''
    });
    setSelectedSections(DEFAULT_SELECTED_SECTIONS);
  };

  const getSectionStats = useCallback((section) => {
    const sectionData = Object.values(weeklyData[section] || {});
    if (sectionData.length === 0) {
      return { total: 0, avgEfficiency: 0, bestWeek: null, totalScrap: 0 };
    }

    const total = sectionData.reduce((sum, w) => sum + w.produced, 0);
    const totalScrap = sectionData.reduce((sum, w) => sum + w.scrap, 0);
    const avgEfficiency = sectionData.reduce((sum, w) =>
      sum + (w.target > 0 ? (w.produced / w.target) * 100 : 0), 0) / sectionData.length;
    const bestWeek = sectionData.reduce((best, w) =>
      w.produced > (best?.produced || 0) ? w : best, null);

    return { total, avgEfficiency, bestWeek, totalScrap };
  }, [weeklyData]);

  const overallStats = useMemo(() => {
    let totalWeeks = 0;
    let totalProduced = 0;
    let totalScrap = 0;
    let totalEfficiency = 0;
    let effCount = 0;

    const sectionsToShow = sectionsList.filter(s => selectedSections.includes(s));

    sectionsToShow.forEach(section => {
      const rows = Object.values(weeklyData[section] || {});
      totalWeeks += rows.length;
      totalProduced += rows.reduce((sum, w) => sum + w.produced, 0);
      totalScrap += rows.reduce((sum, w) => sum + w.scrap, 0);

      // Only fold performance sections into the efficiency average --
      // non-performance sections always have target=0 and would just drag
      // the number down for no reason.
      if (sectionHasPerformance(section)) {
        rows.forEach(w => {
          totalEfficiency += w.target > 0 ? (w.produced / w.target) * 100 : 0;
          effCount++;
        });
      }
    });

    return {
      totalWeeks,
      totalProduced,
      totalScrap,
      avgEfficiency: effCount > 0 ? totalEfficiency / effCount : 0
    };
  }, [weeklyData, sectionsList, selectedSections]);

  const loadMoreRecords = () => {
    const newCount = Math.min(visibleCount + 30, data.length);
    setVisibleCount(newCount);
    setHasMoreData(newCount < data.length);
  };

  const displayedData = useMemo(() => {
    return data.slice(0, visibleCount);
  }, [data, visibleCount]);

  useEffect(() => {
    return () => {
      Object.keys(chartRefs.current).forEach(key => {
        if (chartRefs.current[key]) {
          chartRefs.current[key].destroy();
          chartRefs.current[key] = null;
        }
      });
      Object.keys(dailyChartRefs.current).forEach(key => {
        if (dailyChartRefs.current[key]) {
          dailyChartRefs.current[key].destroy();
          dailyChartRefs.current[key] = null;
        }
      });
    };
  }, []);

  const handleRefresh = () => {
    loadData();
    loadWeeklySummary();
    loadStats();
    loadFilterOptions();
    loadMissingData();
    loadDailyBreakdown();
  };

  // Builds the full HTML injected into <table>...</table> by the Print
  // component. This is a complete report, not a single table:
  //   A) KPI summary strip -- totals for produced, target, overall
  //      efficiency, scrap, business days checked, flagged issues
  //   B) Department Performance Overview -- one rollup row per department
  //   C) Detailed Section Breakdown -- all 18 sections, Produced vs Target
  //      with a mini efficiency bar, Scrap, and Status
  //   D) Highlights -- top performing sections and sections needing
  //      attention, called out in plain language
  //   E) Data Completeness Issues -- only sections with flagged dates,
  //      with the full date breakdown
  // For ASSEMBLY, "no data submitted" dates are excluded from issue counts
  // (matching the on-screen Data Completeness Check), and a section that
  // is missing data with nothing else logged is called out as "No Data"
  // instead of silently reading as "Good".
  const buildPrintTableHTML = () => {
    const startDate = filters.startDate.toISOString().split('T')[0];
    const endDate = filters.endDate.toISOString().split('T')[0];
    const shortDate = (d) => d.slice(5); // '2026-08-11' -> '08-11'

    // Look up each section's aggregate stats (already loaded via loadStats)
    const statsBySection = {};
    (stats || []).forEach(s => { statsBySection[s.Section] = s; });

    // Look up each section's completeness issues (already loaded via loadMissingData)
    const missingBySection = {};
    if (missingData) {
      missingData.sections.forEach(s => { missingBySection[s.section] = s; });
    }

    const barColorFor = (pct) => (pct >= 80 ? '#28a745' : pct >= 60 ? '#e0731c' : '#dc3545');
    const effBar = (pct) => {
      const clamped = Math.max(0, Math.min(100, pct));
      return `<div style="display:flex;align-items:center;gap:5px;">
        <div style="background:#e9ecef;border-radius:3px;overflow:hidden;height:8px;width:46px;flex-shrink:0;">
          <div style="background:${barColorFor(pct)};height:100%;width:${clamped}%;"></div>
        </div>
        <span>${pct.toFixed(1)}%</span>
      </div>`;
    };

    // ---------------- Build one computed row per section (all 18) ----------------
    const rows = [];
    GROUP_ORDER.forEach(group => {
      const meta = GROUP_META[group] || {};
      const groupSections = sectionsList.filter(s => getSectionGroup(s) === group);

      groupSections.forEach(sectionName => {
        const st = statsBySection[sectionName] || {};
        const produced = Number(st.TotalProduced) || 0;
        const target = Number(st.TotalTarget) || 0;
        const scrap = Number(st.TotalScrap) || 0;
        const hasPerf = meta.hasPerformance;
        const efficiency = hasPerf && target > 0 ? (produced / target) * 100 : null;

        const m = missingBySection[sectionName];
        const hideMissing = shouldHideMissingForSection(sectionName);
        const missingDates = m ? (hideMissing ? [] : m.missingDates) : [];
        const zeroProducedDates = m ? m.zeroProducedDates : [];
        const partialZeroDates = m ? m.partialZeroDates : [];
        const rawMissing = m ? m.missingDates.length : 0;
        const issueCount = missingDates.length + zeroProducedDates.length + partialZeroDates.length;
        const isFullyMissing = hideMissing && issueCount === 0 && rawMissing > 0 && produced === 0 && target === 0;

        let status = 'Good';
        let statusColor = '#28a745';
        if (isFullyMissing) {
          status = 'No Data'; statusColor = '#6c757d';
        } else if (issueCount >= 3) {
          status = 'Critical'; statusColor = '#dc3545';
        } else if (issueCount > 0) {
          status = 'Attention'; statusColor = '#e0731c';
        } else if (hasPerf && efficiency !== null) {
          if (efficiency < 60) { status = 'Critical'; statusColor = '#dc3545'; }
          else if (efficiency < 80) { status = 'Attention'; statusColor = '#e0731c'; }
        }

        rows.push({
          group, meta, sectionName,
          shortLabel: sectionName.replace(`${group} - `, ''),
          produced, target, scrap, hasPerf, efficiency,
          missingDates, zeroProducedDates, partialZeroDates, issueCount,
          isFullyMissing, status, statusColor
        });
      });
    });

    // ---------------- KPI totals across all 18 sections ----------------
    const totalProduced = rows.reduce((sum, r) => sum + r.produced, 0);
    const totalScrap = rows.reduce((sum, r) => sum + r.scrap, 0);
    const perfRows = rows.filter(r => r.hasPerf && r.target > 0);
    const totalTargetPerf = perfRows.reduce((sum, r) => sum + r.target, 0);
    const totalProducedPerf = perfRows.reduce((sum, r) => sum + r.produced, 0);
    const overallEfficiency = totalTargetPerf > 0 ? (totalProducedPerf / totalTargetPerf) * 100 : null;
    const totalIssues = rows.reduce((sum, r) => sum + r.issueCount, 0);
    const businessDaysChecked = missingData ? missingData.businessDays.length : 0;
    const flaggedSections = rows.filter(r => r.issueCount > 0 || r.isFullyMissing);

    let html = '';

    // ============ Block A: Report Header + KPI Summary ============
    html += `<thead>
      <tr>
        <th colspan="8" style="text-align:left;font-size:17px;padding:10px 6px 2px;border-bottom:2px solid #000;">
          Production Live Report
        </th>
      </tr>
      <tr>
        <th colspan="8" style="text-align:left;font-size:11px;font-weight:400;color:#555;padding:2px 6px 10px;">
          Period: ${startDate} to ${endDate} &nbsp;|&nbsp; Generated: ${new Date().toLocaleString()}
        </th>
      </tr>
    </thead>`;

    html += `<tbody>
      <tr>
        <td colspan="8" style="padding:6px;">
          <table style="width:100%;border-collapse:collapse;">
            <tr style="background:#f1f3f5;">
              <td style="padding:8px 4px;text-align:center;border:1px solid #ddd;">
                <div style="font-size:17px;font-weight:700;">${totalProduced.toLocaleString()}</div>
                <div style="font-size:9px;color:#666;">TOTAL PRODUCED</div>
              </td>
              <td style="padding:8px 4px;text-align:center;border:1px solid #ddd;">
                <div style="font-size:17px;font-weight:700;">${totalTargetPerf.toLocaleString()}</div>
                <div style="font-size:9px;color:#666;">TOTAL TARGET</div>
              </td>
              <td style="padding:8px 4px;text-align:center;border:1px solid #ddd;">
                <div style="font-size:17px;font-weight:700;color:${overallEfficiency !== null ? barColorFor(overallEfficiency) : '#666'};">${overallEfficiency !== null ? overallEfficiency.toFixed(1) + '%' : '--'}</div>
                <div style="font-size:9px;color:#666;">OVERALL EFFICIENCY</div>
              </td>
              <td style="padding:8px 4px;text-align:center;border:1px solid #ddd;">
                <div style="font-size:17px;font-weight:700;">${totalScrap.toLocaleString()}</div>
                <div style="font-size:9px;color:#666;">TOTAL SCRAP</div>
              </td>
              <td style="padding:8px 4px;text-align:center;border:1px solid #ddd;">
                <div style="font-size:17px;font-weight:700;">${businessDaysChecked}</div>
                <div style="font-size:9px;color:#666;">BUSINESS DAYS CHECKED</div>
              </td>
              <td style="padding:8px 4px;text-align:center;border:1px solid #ddd;">
                <div style="font-size:17px;font-weight:700;color:${totalIssues > 0 ? '#dc3545' : '#28a745'};">${totalIssues}</div>
                <div style="font-size:9px;color:#666;">FLAGGED ISSUES</div>
              </td>
            </tr>
          </table>
        </td>
      </tr>
    </tbody>`;

    // ============ Block B: Department Performance Overview ============
    html += `<tbody>
      <tr><td colspan="8" style="padding:14px 0 0;border:none;"></td></tr>
      <tr>
        <th colspan="8" style="text-align:left;font-size:13px;padding:8px 6px;border-bottom:2px solid #333;">
          Department Performance Overview
        </th>
      </tr>
      <tr style="background:#333;color:#fff;">
        <th style="width:24%;">Department</th>
        <th style="width:13%;">Produced</th>
        <th style="width:13%;">Target</th>
        <th style="width:17%;">Efficiency</th>
        <th style="width:10%;">Scrap</th>
        <th style="width:9%;">Issues</th>
        <th colspan="2" style="width:14%;">Status</th>
      </tr>
    </tbody>`;

    html += '<tbody>';
    let deptRowIndex = 0;
    GROUP_ORDER.forEach(group => {
      const meta = GROUP_META[group] || {};
      const groupRows = rows.filter(r => r.group === group);
      if (groupRows.length === 0) return;

      const gProduced = groupRows.reduce((sum, r) => sum + r.produced, 0);
      const gTarget = groupRows.filter(r => r.hasPerf).reduce((sum, r) => sum + r.target, 0);
      const gScrap = groupRows.reduce((sum, r) => sum + r.scrap, 0);
      const gEfficiency = meta.hasPerformance && gTarget > 0 ? (gProduced / gTarget) * 100 : null;
      const gIssues = groupRows.reduce((sum, r) => sum + r.issueCount, 0);
      const gFullyMissing = groupRows.length > 0 && groupRows.every(r => r.isFullyMissing);

      let gStatus = 'Good';
      let gColor = '#28a745';
      if (gFullyMissing) {
        gStatus = 'No Data'; gColor = '#6c757d';
      } else if (gIssues >= 3) {
        gStatus = 'Critical'; gColor = '#dc3545';
      } else if (gIssues > 0) {
        gStatus = 'Attention'; gColor = '#e0731c';
      } else if (gEfficiency !== null) {
        if (gEfficiency < 60) { gStatus = 'Critical'; gColor = '#dc3545'; }
        else if (gEfficiency < 80) { gStatus = 'Attention'; gColor = '#e0731c'; }
      }

      const rowShade = deptRowIndex % 2 === 1 ? 'background-color:#f7f7f7;' : '';
      deptRowIndex++;

      html += `<tr style="${rowShade}">
        <td style="font-weight:700;">${group}</td>
        <td style="text-align:right;">${gProduced.toLocaleString()}</td>
        <td style="text-align:right;">${meta.hasPerformance ? gTarget.toLocaleString() : '--'}</td>
        <td>${gEfficiency !== null ? effBar(gEfficiency) : '--'}</td>
        <td style="text-align:right;">${gScrap.toLocaleString()}</td>
        <td style="text-align:center;">${gIssues > 0 ? gIssues : '--'}</td>
        <td colspan="2" style="color:${gColor};font-weight:bold;">${gStatus}</td>
      </tr>`;
    });
    html += '</tbody>';

    // ============ Block C: Detailed Section Breakdown ============
    html += `<tbody>
      <tr><td colspan="8" style="padding:14px 0 0;border:none;"></td></tr>
      <tr>
        <th colspan="8" style="text-align:left;font-size:13px;padding:8px 6px;border-bottom:2px solid #333;">
          Detailed Section Breakdown
        </th>
      </tr>
      <tr style="background:#333;color:#fff;">
        <th style="width:14%;">Department</th>
        <th style="width:17%;">Sub-Section</th>
        <th style="width:10%;">Produced</th>
        <th style="width:10%;">Target</th>
        <th style="width:17%;">Efficiency</th>
        <th style="width:8%;">Scrap</th>
        <th style="width:8%;">Issues</th>
        <th style="width:16%;">Status</th>
      </tr>
    </tbody>`;

    html += '<tbody>';
    GROUP_ORDER.forEach(group => {
      const groupRows = rows.filter(r => r.group === group);
      if (groupRows.length === 0) return;

      groupRows.forEach((r, idx) => {
        const rowShade = idx % 2 === 1 ? 'background-color:#f7f7f7;' : '';
        html += `<tr style="${rowShade}">
          <td style="font-weight:${idx === 0 ? 'bold' : 'normal'};">${idx === 0 ? group : ''}</td>
          <td>${r.shortLabel}</td>
          <td style="text-align:right;">${r.produced.toLocaleString()}</td>
          <td style="text-align:right;">${r.hasPerf ? r.target.toLocaleString() : '--'}</td>
          <td>${r.hasPerf && r.efficiency !== null ? effBar(r.efficiency) : '--'}</td>
          <td style="text-align:right;">${r.scrap.toLocaleString()}</td>
          <td style="text-align:center;">${r.issueCount > 0 ? r.issueCount : '--'}</td>
          <td style="color:${r.statusColor};font-weight:bold;">${r.status}</td>
        </tr>`;
      });

      // Thin spacer row between departments for readability
      html += `<tr><td colspan="8" style="padding:2px;border:none;"></td></tr>`;
    });
    html += '</tbody>';

    // ============ Block D: Highlights ============
    const perfSorted = rows.filter(r => r.hasPerf && r.efficiency !== null).sort((a, b) => b.efficiency - a.efficiency);
    const topPerformers = perfSorted.slice(0, 3);
    const attentionRank = (s) => (s === 'Critical' ? 0 : s === 'No Data' ? 1 : 2);
    const needsAttention = rows
      .filter(r => r.status === 'Critical' || r.status === 'Attention' || r.status === 'No Data')
      .sort((a, b) => attentionRank(a.status) - attentionRank(b.status));

    if (topPerformers.length > 0 || needsAttention.length > 0) {
      html += `<tbody>
        <tr><td colspan="8" style="padding:14px 0 0;border:none;"></td></tr>
        <tr>
          <th colspan="8" style="text-align:left;font-size:13px;padding:8px 6px;border-bottom:2px solid #333;">
            Highlights
          </th>
        </tr>`;

      if (topPerformers.length > 0) {
        html += `<tr><td colspan="8" style="font-weight:600;font-size:11px;padding:6px 6px 2px;color:#28a745;">Top Performing Sections</td></tr>`;
        topPerformers.forEach(r => {
          html += `<tr><td colspan="8" style="padding:2px 12px;font-size:11px;">${r.group} - ${r.shortLabel}: ${r.efficiency.toFixed(1)}% efficiency (${r.produced.toLocaleString()} / ${r.target.toLocaleString()})</td></tr>`;
        });
      }

      if (needsAttention.length > 0) {
        html += `<tr><td colspan="8" style="font-weight:600;font-size:11px;padding:8px 6px 2px;color:#dc3545;">Sections Needing Attention</td></tr>`;
        needsAttention.forEach(r => {
          let reason;
          if (r.isFullyMissing) reason = 'no data submitted for this period';
          else if (r.issueCount > 0) reason = `${r.issueCount} flagged date${r.issueCount !== 1 ? 's' : ''}`;
          else if (r.efficiency !== null) reason = `${r.efficiency.toFixed(1)}% efficiency`;
          else reason = '';
          html += `<tr><td colspan="8" style="padding:2px 12px;font-size:11px;color:${r.statusColor};">${r.group} - ${r.shortLabel}: ${r.status} (${reason})</td></tr>`;
        });
      }

      html += '</tbody>';
    }

    // ============ Block E: Data Completeness Issues (flagged only) ============
    if (flaggedSections.length > 0) {
      html += `<tbody>
        <tr><td colspan="8" style="padding:14px 0 0;border:none;"></td></tr>
        <tr>
          <th colspan="8" style="text-align:left;font-size:13px;padding:8px 6px;border-bottom:2px solid #333;">
            Data Completeness Issues
          </th>
        </tr>
        <tr style="background:#333;color:#fff;">
          <th style="width:14%;">Department</th>
          <th style="width:17%;">Sub-Section</th>
          <th colspan="6" style="width:69%;">Flagged Dates</th>
        </tr>`;

      flaggedSections.forEach((r, idx) => {
        const rowShade = idx % 2 === 1 ? 'background-color:#f7f7f7;' : '';
        let breakdown;
        if (r.isFullyMissing) {
          breakdown = `<span style="color:#6c757d;">No data submitted for this period</span>`;
        } else {
          const parts = [];
          if (r.missingDates.length > 0) {
            parts.push(`<span style="color:#dc3545;">No data: ${r.missingDates.map(shortDate).join(', ')}</span>`);
          }
          if (r.zeroProducedDates.length > 0) {
            parts.push(`<span style="color:#e0731c;">0 produced: ${r.zeroProducedDates.map(shortDate).join(', ')}</span>`);
          }
          if (r.partialZeroDates.length > 0) {
            const label = r.partialZeroDates.map(p => `${shortDate(p.date)} (${p.zeroCount}/${p.totalCount})`).join(', ');
            parts.push(`<span style="color:#b8860b;">Partial: ${label}</span>`);
          }
          breakdown = parts.join('<br/>');
        }

        html += `<tr style="${rowShade}">
          <td>${r.group}</td>
          <td>${r.shortLabel}</td>
          <td colspan="6" style="font-size:8px;line-height:1.4;">${breakdown}</td>
        </tr>`;
      });

      html += '</tbody>';
    }

    // ============ Footer ============
    html += `<tbody>
      <tr>
        <td colspan="8" style="padding:16px 6px 4px;border-top:1px solid #ccc;font-size:9px;color:#888;">
          Report generated ${new Date().toLocaleString()}
        </td>
      </tr>
    </tbody>`;

    return html;
  };


  const sectionsToDisplay = sectionsList.filter(s => selectedSections.includes(s));

  // Group the visible sections in fixed GROUP_ORDER, skipping empty groups
  const groupedSectionsToDisplay = GROUP_ORDER
    .map(group => ({
      group,
      meta: GROUP_META[group],
      sections: sectionsToDisplay.filter(s => getSectionGroup(s) === group)
    }))
    .filter(g => g.sections.length > 0);

  const groupedSectionsForFilter = GROUP_ORDER
    .map(group => ({
      group,
      meta: GROUP_META[group],
      sections: sectionsList.filter(s => getSectionGroup(s) === group)
    }))
    .filter(g => g.sections.length > 0);

  return (
    <div className="production-report-container">
      {/* Header */}
      <div className="report-header">
        <div className="header-left">
          <h1>
            <i className="fas fa-chart-line"></i>
            Production Live Report
          </h1>
          <p className="header-subtitle">Weekly production analysis across all 18 sections (Click on any bar for detailed view)</p>
        </div>
        <div className="header-right">
          <button className="btn btn-primary" onClick={() => setPrintRequested(true)} style={{ marginRight: 8 }}>
            <i className="fas fa-print"></i> Print Report
          </button>
          <button className="btn btn-warning" onClick={handleRefresh}>
            <i className="fas fa-sync"></i> Refresh
          </button>
        </div>
      </div>

      {printRequested && (
        <Print
          tableHTML={buildPrintTableHTML()}
          title="Production Live Report"
          onComplete={() => setPrintRequested(false)}
        />
      )}

      {/* Error Message */}
      {error && (
        <div className="error-message">
          <i className="fas fa-exclamation-circle"></i> {error}
        </div>
      )}

      {/* Filters (hidden by default) */}
      {showFilters && (
        <div className="filters-section">
          <div className="filters-grid">
            <div className="filter-group">
              <label><i className="fas fa-calendar"></i> Start Date</label>
              <DatePicker
                selected={filters.startDate}
                onChange={(date) => handleFilterChange('startDate', date)}
                dateFormat="yyyy-MM-dd"
                className="filter-input"
                maxDate={filters.endDate}
                minDate={MIN_DATE}
              />
            </div>
            <div className="filter-group">
              <label><i className="fas fa-calendar"></i> End Date</label>
              <DatePicker
                selected={filters.endDate}
                onChange={(date) => handleFilterChange('endDate', date)}
                dateFormat="yyyy-MM-dd"
                className="filter-input"
                minDate={filters.startDate}
              />
            </div>
            <div className="filter-group">
              <label><i className="fas fa-calendar-alt"></i> Year</label>
              <SearchableSelect
                options={filterOptions.years}
                value={filters.year}
                onChange={(value) => handleFilterChange('year', value)}
                placeholder="All Years"
                label="Year"
              />
            </div>
            <div className="filter-group">
              <label><i className="fas fa-hashtag"></i> Week</label>
              <SearchableSelect
                options={filterOptions.weeks}
                value={filters.week}
                onChange={(value) => handleFilterChange('week', value)}
                placeholder="All Weeks"
                label="Week"
              />
            </div>
            <div className="filter-group">
              <label><i className="fas fa-building"></i> Section</label>
              <SearchableSelect
                options={filterOptions.sections}
                value={filters.section}
                onChange={(value) => handleFilterChange('section', value)}
                placeholder="All Sections"
                label="Section"
              />
            </div>
            <div className="filter-group">
              <label><i className="fas fa-user"></i> Customer</label>
              <SearchableSelect
                options={filterOptions.customers}
                value={filters.customer}
                onChange={(value) => handleFilterChange('customer', value)}
                placeholder="All Customers"
                label="Customer"
              />
            </div>
            <div className="filter-group">
              <label><i className="fas fa-user-cog"></i> Operator</label>
              <SearchableSelect
                options={filterOptions.operators}
                value={filters.operator}
                onChange={(value) => handleFilterChange('operator', value)}
                placeholder="All Operators"
                label="Operator"
              />
            </div>
            <div className="filter-group">
              <label><i className="fas fa-microchip"></i> Workstation</label>
              <SearchableSelect
                options={filterOptions.workstations}
                value={filters.workstation}
                onChange={(value) => handleFilterChange('workstation', value)}
                placeholder="All Workstations"
                label="Workstation"
              />
            </div>
            <div className="filter-group">
              <label><i className="fas fa-cube"></i> Part</label>
              <SearchableSelect
                options={filterOptions.parts}
                value={filters.part}
                onChange={(value) => handleFilterChange('part', value)}
                placeholder="All Parts"
                label="Part"
              />
            </div>
            <div className="filter-group">
              <label><i className="fas fa-clock"></i> Shift</label>
              <SearchableSelect
                options={filterOptions.shifts}
                value={filters.shift}
                onChange={(value) => handleFilterChange('shift', value)}
                placeholder="All Shifts"
                label="Shift"
              />
            </div>
            <div className="filter-actions">
              <button className="btn btn-danger" onClick={resetFilters}>
                <i className="fas fa-times"></i> Reset
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Section Filter Checkboxes, grouped (hidden by default) */}
      <div className="section-filter-container">
        <div className="section-filter-header">
          <button
            className="btn btn-sm btn-outline-primary"
            onClick={() => setShowSectionFilter(!showSectionFilter)}
          >
            <i className={`fas fa-chevron-${showSectionFilter ? 'up' : 'down'}`}></i>
            {showSectionFilter ? 'Hide' : 'Show'} Section Filter
          </button>
          <span className="section-filter-count">
            Showing {sectionsToDisplay.length} of {sectionsList.length} sections
          </span>
          <div className="section-filter-actions">
            <button className="btn btn-sm btn-outline-success" onClick={selectAllSections}>
              Select All
            </button>
            <button className="btn btn-sm btn-outline-danger" onClick={deselectAllSections}>
              Deselect All
            </button>
          </div>
        </div>
        {showSectionFilter && (
          <div className="section-filter-groups">
            {groupedSectionsForFilter.map(({ group, meta, sections }) => (
              <div key={group} className="section-filter-group" style={{ marginBottom: 14 }}>
                <div
                  className="section-filter-group-label"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    color: meta.color,
                    fontWeight: 600,
                    fontSize: 13,
                    marginBottom: 6,
                    paddingBottom: 4,
                    borderBottom: `2px solid ${meta.color}33`
                  }}
                >
                  <span>
                    <i className={`fas ${meta.icon}`} style={{ marginRight: 6 }}></i>
                    {group}
                    {!meta.hasPerformance && (
                      <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 400, opacity: 0.7 }}>
                        (produced count only)
                      </span>
                    )}
                  </span>
                  <span>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-success"
                      style={{ marginRight: 6 }}
                      onClick={() => selectGroupSections(group)}
                    >
                      All
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-danger"
                      onClick={() => deselectGroupSections(group)}
                    >
                      None
                    </button>
                  </span>
                </div>
                <div className="section-filter-grid">
                  {sections.map(section => {
                    const isSelected = selectedSections.includes(section);
                    const color = getSectionColor(section);
                    return (
                      <label key={section} className="section-filter-checkbox" style={{ borderColor: isSelected ? color : '#ddd' }}>
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => handleSectionToggle(section)}
                        />
                        <span className="section-filter-color" style={{ backgroundColor: color }}></span>
                        {section}
                      </label>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Data Completeness Check -- flags weekdays (weekends excluded) at
          the SEATS/BODY/TACKING-STRIP sub-bucket level (not the TOTAL
          rollup, which can mask a bad day behind a good one). Click a date
          badge to see exactly which entries were logged for that section
          on that date. For ASSEMBLY, "no data submitted" isn't tracked --
          only "0 produced" (and partial-zero) days are surfaced. */}
      <div className="missing-data-section" style={{ marginBottom: 28 }}>
        <div
          className="missing-data-header"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '12px 18px',
            borderLeft: '5px solid #dc3545',
            background: 'rgba(220,53,69,0.08)',
            color: '#dc3545',
            fontWeight: 700,
            fontSize: 18,
            borderRadius: 6,
            marginBottom: 12
          }}
        >
          <i className="fas fa-exclamation-triangle"></i>
          <span>Data Completeness Check</span>
          <span style={{ fontSize: 13, fontWeight: 500, opacity: 0.85 }}>
            (Weekdays only -- weekends excluded. Click a date to see details.)
          </span>
          {missingData && (
            <span style={{ marginLeft: 'auto', fontSize: 13, fontWeight: 500, opacity: 0.9 }}>
              {missingData.businessDays.length} business day{missingData.businessDays.length !== 1 ? 's' : ''} checked
            </span>
          )}
        </div>

        {missingDataLoading ? (
          <div className="no-data-message" style={{ padding: 20 }}>
            <i className="fas fa-spinner fa-spin"></i>
            <p>Checking data completeness...</p>
          </div>
        ) : !missingData ? (
          <div className="no-data-message" style={{ padding: 20 }}>
            <i className="fas fa-exclamation-circle"></i>
            <p>Could not load completeness report</p>
          </div>
        ) : missingData.businessDays.length === 0 ? (
          <div className="no-data-message" style={{ padding: 20 }}>
            <i className="fas fa-calendar-times"></i>
            <p>No weekdays in the selected date range</p>
          </div>
        ) : (
          <div className="section-grid">
            {GROUP_ORDER.map(group => {
              const meta = GROUP_META[group] || {};
              const groupSections = missingData.sections.filter(s => s.group === group);
              if (groupSections.length === 0) return null;

              // "No data submitted" isn't tracked for specific sections
              // (Assembly's total, and Rigid Molding's Tacking Strip) --
              // only 0-produced / partial-zero issues matter for those.
              // Other sections in the same department (e.g. Rigid Molding's
              // Seats and Body) keep full missing-data tracking.
              const totalIssues = groupSections.reduce(
                (sum, s) => sum + (shouldHideMissingForSection(s.section) ? 0 : s.missingDates.length) + s.zeroProducedDates.length + s.partialZeroDates.length, 0
              );
              const hasIssues = totalIssues > 0;

              // "No data at all" only reads as a department-wide status when
              // EVERY section in the department hides missing the same way
              // (today, only Assembly's single section qualifies) -- if any
              // section still tracks missing normally, its own badge covers
              // that instead.
              const allSectionsHideMissing = groupSections.every(s => shouldHideMissingForSection(s.section));
              const rawMissingCount = groupSections.reduce((sum, s) => sum + s.missingDates.length, 0);
              const isFullyMissing = allSectionsHideMissing && !hasIssues && rawMissingCount > 0;

              return (
                <div
                  key={group}
                  className="section-card"
                  style={{ borderColor: hasIssues ? '#dc3545' : (isFullyMissing ? '#6c757d' : '#28a745') }}
                >
                  <div className="section-header" style={{ fontSize: 15 }}>
                    <span>
                      <i className={`fas ${meta.icon || 'fa-industry'}`}></i>
                      {group}
                    </span>
                    <span
                      className="badge"
                      style={{ backgroundColor: hasIssues ? '#dc3545' : (isFullyMissing ? '#6c757d' : '#28a745'), color: '#fff', fontSize: 13 }}
                    >
                      {hasIssues
                        ? `${totalIssues} issue${totalIssues !== 1 ? 's' : ''}`
                        : (isFullyMissing ? 'No data' : '0 issues')}
                    </span>
                  </div>
                  <div className="section-body">
                    {isFullyMissing ? (
                      <div style={{ padding: '10px 4px', color: '#6c757d', fontSize: 14 }}>
                        <i className="fas fa-ban"></i> No data submitted for {rawMissingCount} weekday{rawMissingCount !== 1 ? 's' : ''} in this period
                      </div>
                    ) : !hasIssues ? (
                      <div style={{ padding: '10px 4px', color: '#28a745', fontSize: 14 }}>
                        <i className="fas fa-check-circle"></i> All weekdays have data
                      </div>
                    ) : (
                      <div style={{ padding: '6px 4px' }}>
                        {groupSections.map(secReport => {
                          const { section, zeroProducedDates, partialZeroDates } = secReport;
                          const hideMissingForThisSection = shouldHideMissingForSection(section);
                          const missingDates = hideMissingForThisSection ? [] : secReport.missingDates;
                          if (missingDates.length === 0 && zeroProducedDates.length === 0 && partialZeroDates.length === 0) return null;
                          // Strip the "GROUP - " prefix for a cleaner sub-label
                          const shortLabel = section.replace(`${group} - `, '');

                          return (
                            <div key={section} style={{ marginBottom: 14 }}>
                              <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 5, opacity: 0.9 }}>
                                {shortLabel}
                              </div>
                              {missingDates.length > 0 && (
                                <div style={{ marginBottom: 8 }}>
                                  <div style={{ fontSize: 13, fontWeight: 600, color: '#dc3545', marginBottom: 4 }}>
                                    No data submitted ({missingDates.length}):
                                  </div>
                                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7 }}>
                                    {missingDates.map(date => (
                                      <button
                                        key={date}
                                        type="button"
                                        onClick={() => fetchDayDetails(section, date, 'missing')}
                                        style={{
                                          backgroundColor: 'rgba(220,53,69,0.12)',
                                          color: '#dc3545',
                                          border: '1px solid rgba(220,53,69,0.4)',
                                          borderRadius: 4,
                                          padding: '4px 10px',
                                          fontSize: 13,
                                          fontWeight: 600,
                                          cursor: 'pointer'
                                        }}
                                      >
                                        {date}
                                      </button>
                                    ))}
                                  </div>
                                </div>
                              )}
                              {zeroProducedDates.length > 0 && (
                                <div style={{ marginBottom: 8 }}>
                                  <div style={{ fontSize: 13, fontWeight: 600, color: '#e0731c', marginBottom: 4 }}>
                                    All entries 0 produced ({zeroProducedDates.length}):
                                  </div>
                                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7 }}>
                                    {zeroProducedDates.map(date => (
                                      <button
                                        key={date}
                                        type="button"
                                        onClick={() => fetchDayDetails(section, date, 'zero')}
                                        style={{
                                          backgroundColor: 'rgba(224,115,28,0.12)',
                                          color: '#e0731c',
                                          border: '1px solid rgba(224,115,28,0.4)',
                                          borderRadius: 4,
                                          padding: '4px 10px',
                                          fontSize: 13,
                                          fontWeight: 600,
                                          cursor: 'pointer'
                                        }}
                                      >
                                        {date}
                                      </button>
                                    ))}
                                  </div>
                                </div>
                              )}
                              {partialZeroDates.length > 0 && (
                                <div>
                                  <div style={{ fontSize: 13, fontWeight: 600, color: '#b8860b', marginBottom: 4 }}>
                                    Some entries 0 produced ({partialZeroDates.length}):
                                  </div>
                                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7 }}>
                                    {partialZeroDates.map(({ date, zeroCount, totalCount }) => (
                                      <button
                                        key={date}
                                        type="button"
                                        onClick={() => fetchDayDetails(section, date, 'partial')}
                                        title={`${zeroCount} of ${totalCount} entries had 0 produced`}
                                        style={{
                                          backgroundColor: 'rgba(184,134,11,0.12)',
                                          color: '#b8860b',
                                          border: '1px solid rgba(184,134,11,0.4)',
                                          borderRadius: 4,
                                          padding: '4px 10px',
                                          fontSize: 13,
                                          fontWeight: 600,
                                          cursor: 'pointer'
                                        }}
                                      >
                                        {date} ({zeroCount}/{totalCount})
                                      </button>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Day-wise Charts, grouped into 6 clusters.
          - SANDING RIGID / DRILL TAP RIGID / TRIMMING FLEX / ASSEMBLY get a
            multi-color, day-wise chart with one color per OPERATOR (stacked
            by Seat/Body/Tacking Strip where applicable), plus a cumulative
            trend line per operator -- built to make "who did better on
            which day" an at-a-glance comparison.
          - RIGID MOLDING / FLEX MOLDING get a day-wise Target vs Produced
            chart broken out by sub-section (Seat / Body / Tacking Strip). */}
      {groupedSectionsToDisplay.map(({ group, meta, sections }) => {
        const isOperatorGroup = OPERATOR_DAYWISE_GROUPS.includes(group);
        const isTargetGroup = TARGET_DAYWISE_GROUPS.includes(group);
        const operatorGroupData = isOperatorGroup ? dailyOperatorData[group] : null;
        const targetGroupData = isTargetGroup ? dailyTargetData[group] : null;

        // Ranked operator totals (Produced) for the small comparison legend.
        // "operator" here is the normalized key; "displayName" is the
        // best-looking casing seen for that key (merges e.g. "Madhu Bala"
        // and "MADHU BALA" into one entry).
        let operatorTotals = [];
        if (isOperatorGroup && operatorGroupData) {
          operatorTotals = operatorGroupData.operators
            .map(op => {
              const total = operatorGroupData.days.reduce(
                (sum, d) => sum + ((operatorGroupData.byDay[d] && operatorGroupData.byDay[d][op]) ? operatorGroupData.byDay[d][op].total : 0),
                0
              );
              return { operator: op, displayName: (operatorGroupData.displayNames && operatorGroupData.displayNames[op]) || op, total };
            })
            .sort((a, b) => b.total - a.total);
        }

        return (
          <div key={group} className="section-group-block" style={{ marginBottom: 28 }}>
            <div
              className="section-group-header"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '10px 16px',
                borderLeft: `5px solid ${meta.color}`,
                background: `${meta.color}14`,
                color: meta.color,
                fontWeight: 700,
                fontSize: 17,
                borderRadius: 6,
                marginBottom: 12
              }}
            >
              <i className={`fas ${meta.icon}`}></i>
              <span>{group}</span>
              <span style={{ fontSize: 13, fontWeight: 500, opacity: 0.8 }}>
                {isOperatorGroup
                  ? '(Day-wise, by operator -- Produced)'
                  : isTargetGroup
                    ? '(Day-wise, by sub-section -- Target vs Produced)'
                    : (meta.hasPerformance ? '(Target vs Produced + Efficiency)' : '(Produced count only)')}
              </span>
              <span style={{ marginLeft: 'auto', fontSize: 13, fontWeight: 500, opacity: 0.85 }}>
                {(() => {
                  // The count shown here must match what the chart actually
                  // plots -- the TOTAL rollup section is never charted in
                  // the day-wise view, so it shouldn't be counted either.
                  const chartedCount = isOperatorGroup
                    ? (operatorGroupData ? operatorGroupData.subLabels.length : sections.filter(s => !s.endsWith('- TOTAL')).length || sections.length)
                    : isTargetGroup
                      ? (targetGroupData ? targetGroupData.subLabels.length : sections.filter(s => !s.endsWith('- TOTAL')).length)
                      : sections.length;
                  return `${chartedCount} sub-section${chartedCount !== 1 ? 's' : ''}`;
                })()}
              </span>
            </div>

            <div className="section-card" style={{ padding: 12 }}>
              <div
                className="chart-container"
                style={{ height: 340, overflowX: 'auto', overflowY: 'hidden' }}
              >
                {dailyBreakdownLoading ? (
                  <div className="no-data-message">
                    <i className="fas fa-spinner fa-spin"></i>
                    <p>Loading day-wise data...</p>
                  </div>
                ) : isOperatorGroup ? (
                  operatorGroupData && operatorGroupData.days.length > 0 ? (
                    <div id={`daily-op-chart-wrap-${group.replace(/[^a-zA-Z0-9]/g, '-')}`} style={{ height: '100%' }}>
                      <canvas id={`daily-op-chart-${group.replace(/[^a-zA-Z0-9]/g, '-')}`}></canvas>
                    </div>
                  ) : (
                    <div className="no-data-message">
                      <i className="fas fa-chart-bar"></i>
                      <p>No data available for this department in the selected range</p>
                    </div>
                  )
                ) : isTargetGroup ? (
                  targetGroupData && targetGroupData.days.length > 0 ? (
                    <div id={`daily-target-chart-wrap-${group.replace(/[^a-zA-Z0-9]/g, '-')}`} style={{ height: '100%' }}>
                      <canvas id={`daily-target-chart-${group.replace(/[^a-zA-Z0-9]/g, '-')}`}></canvas>
                    </div>
                  ) : (
                    <div className="no-data-message">
                      <i className="fas fa-chart-bar"></i>
                      <p>No data available for this department in the selected range</p>
                    </div>
                  )
                ) : (
                  <div className="no-data-message">
                    <i className="fas fa-chart-bar"></i>
                    <p>No chart configured for this department</p>
                  </div>
                )}
              </div>

              {/* Operator comparison legend -- ranked totals so it's easy to
                  see who produced more without reading bar heights. The
                  small line preview matches each operator's dash pattern on
                  the trend line (their identifier there, since color no
                  longer varies by operator). */}
              {isOperatorGroup && operatorTotals.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginTop: 10, paddingTop: 10, borderTop: '1px solid #eee' }}>
                  {operatorTotals.map(({ operator, displayName, total }, idx) => {
                    const opIndex = operatorGroupData.operators.indexOf(operator);
                    const dash = getOperatorDash(opIndex).join(',');
                    return (
                      <div
                        key={operator}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 7,
                          fontSize: 14,
                          padding: '5px 12px',
                          borderRadius: 16,
                          background: idx === 0 ? 'rgba(40,167,69,0.10)' : '#f4f4f6',
                          border: idx === 0 ? '1px solid rgba(40,167,69,0.4)' : '1px solid #e2e2e2'
                        }}
                      >
                        <span style={{ fontSize: 12, fontWeight: 700, color: '#888' }}>#{idx + 1}</span>
                        <svg width="22" height="12" style={{ flexShrink: 0 }}>
                          <line x1="0" y1="6" x2="22" y2="6" stroke={meta.color} strokeWidth="2.5" strokeDasharray={dash} />
                        </svg>
                        <strong>{displayName}</strong>
                        <span style={{ opacity: 0.75 }}>{total.toLocaleString()} units</span>
                        {idx === 0 && <i className="fas fa-trophy" style={{ color: '#e0a716', fontSize: 13 }}></i>}
                      </div>
                    );
                  })}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 14, fontSize: 13, opacity: 0.8, marginLeft: 'auto' }}>
                    {operatorGroupData.subLabels.map(subLabel => (
                      <span key={subLabel} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                        <span style={{ width: 12, height: 12, borderRadius: 2, backgroundColor: getSubpartBarColor(subLabel), display: 'inline-block' }}></span>
                        {subLabel}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Sub-section legend for the Target-vs-Produced groups */}
              {isTargetGroup && targetGroupData && targetGroupData.subLabels.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, marginTop: 10, paddingTop: 10, borderTop: '1px solid #eee' }}>
                  {targetGroupData.subLabels.map(subLabel => {
                    const totals = targetGroupData.days.reduce((acc, d) => {
                      const entry = targetGroupData.byDay[d] && targetGroupData.byDay[d][subLabel];
                      if (entry) { acc.target += entry.target; acc.produced += entry.produced; }
                      return acc;
                    }, { target: 0, produced: 0 });
                    const eff = totals.target > 0 ? ((totals.produced / totals.target) * 100).toFixed(1) : '--';
                    return (
                      <div key={subLabel} style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 14 }}>
                        <span style={{ width: 12, height: 12, borderRadius: 2, backgroundColor: subpartColor(subLabel, 'produced'), display: 'inline-block' }}></span>
                        <strong>{subLabel}</strong>
                        <span style={{ opacity: 0.75 }}>{totals.produced.toLocaleString()} / {totals.target.toLocaleString()} ({eff}%)</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        );
      })}

      {/* Data Table */}
      <div className="table-container">
        <h3 className="table-title">
          <i className="fas fa-table"></i> Detailed Production Data
          <span className="record-count">
            Showing {displayedData.length} of {data.length} records
          </span>
        </h3>
        <div className="table-wrapper">
          <table>
            <thead>
              <tr>
                <th>Week</th>
                <th>Date</th>
                <th>Section</th>
                <th>Workstation</th>
                <th>Operator</th>
                <th>Part</th>
                <th>Customer</th>
                <th>Target</th>
                <th>Produced</th>
                <th>Scrap</th>
                <th>Efficiency</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="12" className="loading-cell">
                    <i className="fas fa-spinner fa-spin"></i> Loading data...
                  </td>
                </tr>
              ) : !Array.isArray(data) || data.length === 0 ? (
                <tr>
                  <td colSpan="12" className="empty-cell">
                    <i className="fas fa-inbox"></i> No data found
                  </td>
                </tr>
              ) : (
                displayedData.map((record, index) => {
                  const efficiency = record.Target > 0
                    ? ((record.Produced / record.Target) * 100).toFixed(1)
                    : 0;
                  const status = efficiency >= 80 ? 'success' :
                    efficiency >= 60 ? 'warning' : 'danger';
                  const missingProduction = isMissingProduction(record);

                  return (
                    <tr
                      key={record.SR_No ?? index}
                      style={missingProduction ? { backgroundColor: 'rgba(220,53,69,0.10)' } : undefined}
                    >
                      <td>Wk {record.WeekNumber}</td>
                      <td>{record.FormattedDate}</td>
                      <td>{record.Section || 'N/A'}</td>
                      <td>{record.work_station}</td>
                      <td>{record.Operator}</td>
                      <td>{record.PartNo}</td>
                      <td>{record.Customer}</td>
                      <td>{record.Target}</td>
                      <td>{record.Produced}</td>
                      <td>{record.Scrap}</td>
                      <td>{efficiency}%</td>
                      <td>
                        <span className={`status-badge status-${status}`}>
                          {status === 'success' ? '✓ On Track' :
                            status === 'warning' ? '⚠ Needs Attention' :
                              '✗ Below Target'}
                        </span>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {hasMoreData && !loading && (
          <div className="load-more-container">
            <button className="btn btn-primary" onClick={loadMoreRecords}>
              <i className="fas fa-plus"></i> Load More Records
            </button>
            <span className="load-more-info">
              Showing {visibleCount} of {data.length} records
            </span>
          </div>
        )}

        {data.length === 0 && !loading && !error && (
          <div className="empty-state">
            <i className="fas fa-database"></i>
            <p>No production data available for the selected filters</p>
          </div>
        )}
      </div>

      {/* Week/Section Modal */}
      {modalData.isOpen && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                <i className="fas fa-calendar-week"></i>
                Week {modalData.week} - {modalData.year}
                <span className="modal-section-badge">{modalData.section}</span>
              </h2>
              <button className="modal-close" onClick={closeModal}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="modal-body">
              {modalData.loading ? (
                <div className="modal-loading">
                  <i className="fas fa-spinner fa-spin"></i>
                  <p>Loading detailed data...</p>
                </div>
              ) : modalData.records.length === 0 ? (
                <div className="modal-empty">
                  <i className="fas fa-inbox"></i>
                  <p>No detailed records found for this week</p>
                </div>
              ) : (
                <div className="modal-table-wrapper">
                  <table className="modal-table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Workstation</th>
                        <th>Operator</th>
                        <th>Part No</th>
                        <th>Customer</th>
                        <th>Target</th>
                        <th>Produced</th>
                        <th>Scrap</th>
                        <th>Efficiency</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {modalData.records.map((record, idx) => {
                        const efficiency = record.Target > 0
                          ? ((record.Produced / record.Target) * 100).toFixed(1)
                          : 0;
                        const status = efficiency >= 80 ? 'success' :
                          efficiency >= 60 ? 'warning' : 'danger';
                        const missingProduction = isMissingProduction(record);

                        return (
                          <tr
                            key={record.SR_No ?? idx}
                            style={missingProduction ? { backgroundColor: 'rgba(220,53,69,0.10)' } : undefined}
                          >
                            <td>{record.FormattedDate}</td>
                            <td>{record.work_station}</td>
                            <td>{record.Operator}</td>
                            <td>{record.Part_internalNo}</td>
                            <td>{record.Customer}</td>
                            <td>{record.Target}</td>
                            <td>{record.Produced}</td>
                            <td>{record.Scrap}</td>
                            <td>{efficiency}%</td>
                            <td>
                              <span className={`status-badge status-${status}`}>
                                {status === 'success' ? '✓' :
                                  status === 'warning' ? '⚠' : '✗'}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      <tr className="modal-summary-row">
                        <td colSpan="4"><strong>Summary</strong></td>
                        <td><strong>Total Target</strong></td>
                        <td><strong>{modalData.records.reduce((sum, r) => sum + (Number(r.Target) || 0), 0)}</strong></td>
                        <td><strong>{modalData.records.reduce((sum, r) => sum + (Number(r.Produced) || 0), 0)}</strong></td>
                        <td><strong>{modalData.records.reduce((sum, r) => sum + (Number(r.Scrap) || 0), 0)}</strong></td>
                        <td colSpan="2">
                          <strong>
                            {modalData.records.reduce((sum, r) => sum + (Number(r.Produced) || 0), 0) > 0
                              ? (((modalData.records.reduce((sum, r) => sum + (Number(r.Produced) || 0), 0) /
                                modalData.records.reduce((sum, r) => sum + (Number(r.Target) || 0), 0)) * 100).toFixed(1) + '%')
                              : '0%'}
                          </strong>
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <span className="modal-record-count">
                {modalData.records.length} records found
              </span>
              <button className="btn btn-primary" onClick={closeModal}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Day-Detail Modal -- opens when a missing/all-zero/partial-zero date
          badge in the Data Completeness Check section is clicked, or when a
          bar/point on a day-wise chart is clicked (scoped to that exact
          operator + sub-section + day, or operator + day for a line point) */}
      {dayModalData.isOpen && (
        <div className="modal-overlay" onClick={closeDayModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                <i className="fas fa-calendar-day"></i>
                {dayModalData.date}
                <span className="modal-section-badge">{dayModalData.section}</span>
                {dayModalData.operator && (
                  <span className="modal-section-badge" style={{ marginLeft: 6, backgroundColor: '#333' }}>
                    <i className="fas fa-user" style={{ marginRight: 4 }}></i>{dayModalData.operator}
                  </span>
                )}
              </h2>
              <button className="modal-close" onClick={closeDayModal}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="modal-body">
              {dayModalData.loading ? (
                <div className="modal-loading">
                  <i className="fas fa-spinner fa-spin"></i>
                  <p>Loading entries for this date...</p>
                </div>
              ) : dayModalData.records.length === 0 ? (
                <div className="modal-empty">
                  <i className="fas fa-inbox"></i>
                  <p>
                    {dayModalData.issueType === 'missing'
                      ? `No entries were logged for ${dayModalData.section} on ${dayModalData.date} -- confirms this date is genuinely missing.`
                      : dayModalData.operator
                        ? `No entries found for ${dayModalData.operator} on ${dayModalData.date} for ${dayModalData.section}.`
                        : 'No entries found.'}
                  </p>
                </div>

              ) : (
                <div className="modal-table-wrapper">
                  <table className="modal-table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Workstation</th>
                        <th>Operator</th>
                        <th>Part No</th>
                        <th>Customer</th>
                        <th>Target</th>
                        <th>Produced</th>
                        <th>Scrap</th>
                        <th>Efficiency</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dayModalData.records.map((record, idx) => {
                        const efficiency = record.Target > 0
                          ? ((record.Produced / record.Target) * 100).toFixed(1)
                          : 0;
                        const missingProduction = isMissingProduction(record);
                        return (
                          <tr
                            key={record.SR_No ?? idx}
                            style={missingProduction ? { backgroundColor: 'rgba(220,53,69,0.10)' } : undefined}
                          >
                            <td>{record.FormattedDate}</td>
                            <td>{record.work_station}</td>
                            <td>{record.Operator}</td>
                            <td>{record.Part_internalNo}</td>
                            <td>{record.Customer}</td>
                            <td>{record.Target}</td>
                            <td>{record.Produced}</td>
                            <td>{record.Scrap}</td>
                            <td>{efficiency}%</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <span className="modal-record-count">
                {dayModalData.records.length} records found
              </span>
              <button className="btn btn-primary" onClick={closeDayModal}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductionReport;